import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ControlPanel.css";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { Col, Row, Modal, ModalBody } from "reactstrap";
import {
  Table,
  Flex,
  Empty,
  Select,
  Input,
  Tabs,
  Tag,
  Descriptions,
  Timeline,
  Card as AntCard,
  Badge as AntBadge,
  Tooltip,
  Statistic,
  Checkbox,
  DatePicker,
  Collapse,
  InputNumber,
  Divider,
  Switch,
} from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import {
  AppstoreOutlined,
  TeamOutlined,
  TagOutlined,
  CalculatorOutlined,
  SafetyOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  WalletOutlined,
  DeleteOutlined,
  CalendarOutlined,
  GiftOutlined,
  ReloadOutlined,
  LoadingOutlined,
  FilePdfOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import misc from "../../../api/misc/miscService";
import type {
  CustomerDetail,
  BillingConfigResponse,
  ModuleAccessItem,
  UserLimitsResponse,
  TaxConfigResponse,
  AccessControlStatus,
  AccessControlLog,
} from "../../../types/super-admin";

// --- Options ---
const billingTypeOptions = [
  { value: "Module", label: "Module" },
  { value: "Bundled", label: "Bundled" },
];
const billingModeOptions = [
  { value: "Auto", label: "Auto" },
  { value: "Manual", label: "Manual" },
];
const billingFrequencyOptions = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Half-Yearly", label: "Half-Yearly" },
  { value: "Yearly", label: "Yearly" },
];
const advanceAdjustmentModeOptions = [
  { value: "Full Adjustment", label: "Full Adjustment" },
  { value: "Partial Adjustment", label: "Partial Adjustment" },
];
const advanceAdjustmentPriorityOptions = [
  { value: "Before Discount", label: "Before Discount" },
  { value: "After Discount", label: "After Discount" },
];
const userLimitTypeOptions = [
  { value: "Restricted", label: "Restricted" },
  { value: "Unlimited", label: "Unlimited" },
];

interface CustomerControlPanelProps {
  partyId?: number;
  embedded?: boolean;
}

const CustomerControlPanel: React.FC<CustomerControlPanelProps> = ({ partyId: propPartyId, embedded = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const customerId = propPartyId || Number(id);

  const initialTab = searchParams.get("tab") || "billing";
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);

  // ---- Billing Config ----
  const [billingConfig, setBillingConfig] = useState<BillingConfigResponse | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingEditMode, setBillingEditMode] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const billingForm = useForm({ mode: "onChange" });

  // ---- Module Access ----
  const [moduleAccess, setModuleAccess] = useState<ModuleAccessItem[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [assignModuleLoading, setAssignModuleLoading] = useState(false);
  const [showAssignSection, setShowAssignSection] = useState(false);
  const [pricingRecords, setPricingRecords] = useState<any[]>([]);
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [assignValidFrom, setAssignValidFrom] = useState<string>("");
  const [assignValidTo, setAssignValidTo] = useState<string>("");
  const [assignCustomPrice, setAssignCustomPrice] = useState<string>("");
  const [modifyingAppId, setModifyingAppId] = useState<number | null>(null);
  const [modifyModulesList, setModifyModulesList] = useState<{ moduleId: number; moduleName: string; isAssigned: boolean }[]>([]);
  const [modifyModulesLoading, setModifyModulesLoading] = useState(false);
  const [editingAppAccess, setEditingAppAccess] = useState<{ appId: number; validFrom: string; validTo: string; customPrice: string } | null>(null);
  const [editingBundleAccess, setEditingBundleAccess] = useState<{ customerBundleId: number; validFrom: string; validTo: string; effectivePrice: string } | null>(null);
  const [editAccessSaving, setEditAccessSaving] = useState(false);
  const [moduleAccessLogs, setModuleAccessLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [revokingAppId, setRevokingAppId] = useState<number | null>(null);
  const [revokingBundleId, setRevokingBundleId] = useState<number | null>(null);

  // ---- User Limits ----
  const [userLimits, setUserLimits] = useState<UserLimitsResponse | null>(null);
  const [userLimitsLoading, setUserLimitsLoading] = useState(false);
  const [userLimitsEditMode, setUserLimitsEditMode] = useState(false);
  const [userLimitsSaving, setUserLimitsSaving] = useState(false);
  const userLimitsForm = useForm({ mode: "onChange" });

  // ---- Coupon ----
  const [linkedCoupon, setLinkedCoupon] = useState<CustomerDetail["linkedCoupon"]>(null);

  // ---- Tax Config ----
  const [taxConfig, setTaxConfig] = useState<TaxConfigResponse | null>(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxEditMode, setTaxEditMode] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxOptions, setTaxOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedTaxId, setSelectedTaxId] = useState<number | undefined>(undefined);

  // ---- Access Control ----
  const [accessStatus, setAccessStatus] = useState<AccessControlStatus | null>(null);
  const [accessLogs, setAccessLogs] = useState<AccessControlLog[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [holdModal, setHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdLoading, setHoldLoading] = useState(false);
  const [autoHoldEnabled, setAutoHoldEnabled] = useState(false);
  const [autoHoldDays, setAutoHoldDays] = useState<number | null>(null);
  const [autoHoldSaving, setAutoHoldSaving] = useState(false);

  // ---- Invoice Report ----
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState<any>(null);
  const [invoiceViewModal, setInvoiceViewModal] = useState(false);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<any>(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

  // ---- Create Invoice ----
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [createInvoiceLoading, setCreateInvoiceLoading] = useState(false);
  const [selectedInvoiceAppIds, setSelectedInvoiceAppIds] = useState<number[]>([]);
  const [selectedInvoiceBundleIds, setSelectedInvoiceBundleIds] = useState<number[]>([]);
  const [customerBundles, setCustomerBundles] = useState<any[]>([]);
  const [invoiceBillingPeriod, setInvoiceBillingPeriod] = useState("");
  const [invoiceDiscountType, setInvoiceDiscountType] = useState<string | undefined>(undefined);
  const [invoiceDiscountValue, setInvoiceDiscountValue] = useState<number | undefined>(undefined);
  const [invoiceDiscountReason, setInvoiceDiscountReason] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [invoiceAdvanceBalance, setInvoiceAdvanceBalance] = useState(0);
  const [invoiceUseAdvance, setInvoiceUseAdvance] = useState(false);
  const [invoiceAdvanceToAdjust, setInvoiceAdvanceToAdjust] = useState<number | undefined>(undefined);

  // ---- Payment Report ----
  const [receivableDetail, setReceivableDetail] = useState<any>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // ---- Advance Payment Form ----
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advFormAmount, setAdvFormAmount] = useState<number | null>(null);
  const [advFormDate, setAdvFormDate] = useState<string>("");
  const [advFormMethod, setAdvFormMethod] = useState<string | undefined>(undefined);
  const [advFormRefNo, setAdvFormRefNo] = useState("");
  const [advFormBank, setAdvFormBank] = useState("");
  const [advFormRemarks, setAdvFormRemarks] = useState("");
  const [advFormSaving, setAdvFormSaving] = useState(false);
  const [editingAdvanceId, setEditingAdvanceId] = useState<number | null>(null);
  const [editAdvData, setEditAdvData] = useState<any>({});

  // ---- Record Payment Form ----
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [rpInvoiceId, setRpInvoiceId] = useState<number | undefined>(undefined);
  const [rpDate, setRpDate] = useState<any>(null);
  const [rpAmount, setRpAmount] = useState<number | undefined>(undefined);
  const [rpMethod, setRpMethod] = useState<string | undefined>(undefined);
  const [rpReference, setRpReference] = useState("");
  const [rpBankName, setRpBankName] = useState("");
  const [rpRemarks, setRpRemarks] = useState("");
  const [rpSaving, setRpSaving] = useState(false);

  const paymentMethodOptions = [
    { value: "BankTransfer", label: "Bank Transfer" },
    { value: "NEFT", label: "NEFT" },
    { value: "RTGS", label: "RTGS" },
    { value: "IMPS", label: "IMPS" },
    { value: "UPI", label: "UPI" },
    { value: "Cheque", label: "Cheque" },
    { value: "Cash", label: "Cash" },
    { value: "Card", label: "Card" },
    { value: "Other", label: "Other" },
  ];

  const handleRecordPayment = async () => {
    if (!rpInvoiceId || !rpDate || !rpAmount || !rpMethod || !rpReference) return;
    setRpSaving(true);
    try {
      const res = await superAdminService.recordPayment({
        customerId,
        invoiceId: rpInvoiceId,
        paymentDate: rpDate.format("YYYY-MM-DD"),
        amount: rpAmount,
        method: rpMethod,
        referenceNumber: rpReference,
        bankName: rpBankName || undefined,
        remarks: rpRemarks || undefined,
      });
      if (res.status) {
        Swal.fire("Success", "Payment recorded successfully", "success");
        setShowRecordPayment(false);
        setRpInvoiceId(undefined);
        setRpDate(null);
        setRpAmount(undefined);
        setRpMethod(undefined);
        setRpReference("");
        setRpBankName("");
        setRpRemarks("");
        fetchPayments();
        fetchInvoices();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setRpSaving(false);
    }
  };

  // ---- Coupon Link ----
  const [couponDropdown, setCouponDropdown] = useState<{ value: number; label: string }[]>([]);
  const [linkCouponModal, setLinkCouponModal] = useState(false);
  const [linkCouponLoading, setLinkCouponLoading] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<number | undefined>(undefined);
  const [couponReferenceFrom, setCouponReferenceFrom] = useState("");
  const [couponHistory, setCouponHistory] = useState<any[]>([]);
  const [couponHistoryLoading, setCouponHistoryLoading] = useState(false);

  // Track which tabs have been loaded (for initial load of dropdowns only)
  const loadedDropdowns = useRef<Set<string>>(new Set());

  // =============================================
  // Fetch customer details
  // =============================================
  const fetchCustomer = useCallback(async (showPageLoader = true) => {
    try {
      if (showPageLoader) setPageLoading(true);
      const res = await superAdminService.getCustomerById(customerId);
      if (res.status && res.data) {
        setCustomer(res.data);
        setLinkedCoupon(res.data.linkedCoupon || null);
        // Load billing + tax config immediately after customer is fetched
        fetchBillingConfig();
        fetchTaxConfig();
        if (!loadedDropdowns.current.has("tax")) {
          loadedDropdowns.current.add("tax");
          loadTaxOptions();
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch customer:", error.message);
    } finally {
      if (showPageLoader) setPageLoading(false);
    }
  }, [customerId]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCustomer();
  }, [fetchCustomer]);

  // Load data for initial tab if navigated with ?tab= param
  const initialTabLoaded = useRef(false);
  useEffect(() => {
    if (!pageLoading && !initialTabLoaded.current && initialTab !== "billing") {
      initialTabLoaded.current = true;
      handleTabChange(initialTab);
    }
  }, [pageLoading]);

  // =============================================
  // Tab change handler — lazy load data
  // =============================================
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    switch (key) {
      case "billing":
        fetchBillingConfig();
        fetchTaxConfig();
        if (!loadedDropdowns.current.has("tax")) {
          loadedDropdowns.current.add("tax");
          loadTaxOptions();
        }
        break;
      case "modules":
        fetchModuleAccess();
        fetchAssignedBundles();
        fetchModuleAccessLogs();
        // Auto-populate dates from billing config
        if (billingConfig?.billingStartDate) {
          setAssignValidFrom(moment(billingConfig.billingStartDate).format("YYYY-MM-DD"));
          setBundleValidFrom(moment(billingConfig.billingStartDate).format("YYYY-MM-DD"));
        }
        if (billingConfig?.billingEndDate) {
          setAssignValidTo(moment(billingConfig.billingEndDate).format("YYYY-MM-DD"));
          setBundleValidTo(moment(billingConfig.billingEndDate).format("YYYY-MM-DD"));
        }
        if (!loadedDropdowns.current.has("modules")) {
          loadedDropdowns.current.add("modules");
          loadDropdowns();
          loadBundleDropdown();
        }
        break;
      case "userLimits":
        fetchUserLimits();
        break;
      case "coupons":
        fetchCouponHistory();
        break;
      case "access":
        fetchAccessControl();
        if (billingConfig) {
          setAutoHoldEnabled(!!billingConfig.autoHoldEnabled);
          setAutoHoldDays(billingConfig.autoHoldDays ?? null);
        }
        break;
      case "invoices":
        fetchInvoices();
        break;
      case "payments":
        fetchPayments();
        break;
    }
  };

  // Billing is now loaded inside fetchCustomer to prevent duplicate calls

  // =============================================
  // Billing Configuration
  // =============================================
  const fetchBillingConfig = useCallback(async () => {
    setBillingLoading(true);
    try {
      const res = await superAdminService.getBillingConfig(customerId);
      if (res.status && res.data) setBillingConfig(res.data);
    } catch (error: any) {
      console.error("Failed to fetch billing config:", error.message);
    } finally {
      setBillingLoading(false);
    }
  }, [customerId]);

  const handleActivateCustomer = async () => {
    const result = await Swal.fire({
      title: "Activate Customer?",
      text: "This will grant module access to this organization. Make sure modules and billing dates are configured.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Activate",
      confirmButtonColor: "#28a745",
    });
    if (!result.isConfirmed) return;
    try {
      console.log("[CustomerControlPanel] Activating customer:", customerId);
      const res = await superAdminService.activateCustomer(customerId);
      console.log("[CustomerControlPanel] Activate response:", res);
      if (res.status) {
        Swal.fire("Activated!", res.message || "Customer activated successfully.", "success");
        fetchCustomer(false);
      } else {
        Swal.fire("Cannot Activate", res.message || "Activation failed.", "warning");
      }
    } catch (error: any) {
      console.error("[CustomerControlPanel] Activate error:", error);
      Swal.fire("Error", error.message || "Failed to activate customer.", "error");
    }
  };

  const startBillingEdit = () => {
    if (!billingConfig) return;
    billingForm.reset({
      billingMode: billingConfig.billingMode || "Auto",
      billingFrequency: billingConfig.billingFrequency || "Monthly",
      billingStartDate: billingConfig.billingStartDate ? dayjs(billingConfig.billingStartDate) : null,
      billingEndDate: billingConfig.billingEndDate ? dayjs(billingConfig.billingEndDate) : null,
      invoiceAutoTriggerDate: billingConfig.invoiceAutoTriggerDate || undefined,
      paymentDueDays: billingConfig.paymentDueDays || 15,
      advancePaymentAllowed: billingConfig.advancePaymentAllowed ? "true" : "false",
    });
    setBillingEditMode(true);
  };

  const saveBillingConfig = async (formData: any) => {
    setBillingSaving(true);
    try {
      const payload: any = { customerId };
      const dirty = billingForm.formState.dirtyFields;
      if (dirty.billingMode) payload.billingMode = formData.billingMode;
      if (dirty.billingFrequency) payload.billingFrequency = formData.billingFrequency;
      if (dirty.billingStartDate)
        payload.billingStartDate = formData.billingStartDate
          ? new Date(formData.billingStartDate).toLocaleDateString("en-CA")
          : undefined;
      if (dirty.billingEndDate)
        payload.billingEndDate = formData.billingEndDate
          ? new Date(formData.billingEndDate).toLocaleDateString("en-CA")
          : undefined;
      if (dirty.invoiceAutoTriggerDate)
        payload.invoiceAutoTriggerDate = Number(formData.invoiceAutoTriggerDate);
      if (dirty.paymentDueDays)
        payload.paymentDueDays = Number(formData.paymentDueDays);
      if (dirty.advancePaymentAllowed)
        payload.advancePaymentAllowed = formData.advancePaymentAllowed === "true";

      if (Object.keys(payload).length <= 1) {
        setBillingEditMode(false);
        return;
      }

      const res = await superAdminService.updateBillingConfig(payload);
      if (res.status) {
        Swal.fire("Updated!", "Billing configuration updated.", "success");
        setBillingEditMode(false);
        fetchBillingConfig();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setBillingSaving(false);
    }
  };

  // =============================================
  // Module Accessibility
  // =============================================
  const fetchModuleAccess = useCallback(async () => {
    setModulesLoading(true);
    try {
      const res = await superAdminService.getModuleAccess(customerId);
      if (res.status && res.data) setModuleAccess(Array.isArray(res.data) ? res.data : []);
    } catch (error: any) {
      console.error("Failed to fetch module access:", error.message);
    } finally {
      setModulesLoading(false);
    }
  }, [customerId]);

  const handlePricingChange = (pricingId: number) => {
    setSelectedPricingId(pricingId);
    const record = pricingRecords.find((p: any) => p.modulePriceId === pricingId);
    setSelectedAppId(record ? record.appId : null);
  };

  const loadDropdowns = () => {
    if (pricingRecords.length === 0) {
      superAdminService.getPricingList({ status: "Active", pageSize: 1000 }).then((res: any) => {
        if (res.status && res.data) {
          const records = Array.isArray(res.data) ? res.data : (res.data?.list || []);
          setPricingRecords(records);
        }
      });
    }
  };

  const resetAssignSection = () => {
    setShowAssignSection(false);
    setSelectedPricingId(null);
    setSelectedAppId(null);
    setAssignValidFrom(billingConfig?.billingStartDate ? moment(billingConfig.billingStartDate).format("YYYY-MM-DD") : "");
    setAssignValidTo(billingConfig?.billingEndDate ? moment(billingConfig.billingEndDate).format("YYYY-MM-DD") : "");
    setAssignCustomPrice("");
  };

  const handleBulkAssign = async () => {
    if (!selectedAppId || !selectedPricingId || !assignValidFrom || !assignValidTo) {
      Swal.fire("Warning", "Please select a pricing plan and date range.", "warning");
      return;
    }
    const selectedRecord = pricingRecords.find((p: any) => p.modulePriceId === selectedPricingId);
    const appLabel = selectedRecord?.pricingName || selectedRecord?.application?.appName || "this application";
    const confirm = await Swal.fire({
      title: "Assign All Modules?",
      text: `This will assign all modules for "${appLabel}" to this customer.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, assign",
    });
    if (!confirm.isConfirmed) return;

    setAssignModuleLoading(true);
    try {
      const payload: any = {
        customerId,
        appId: selectedAppId,
        validFrom: assignValidFrom,
        validTo: assignValidTo,
      };
      if (assignCustomPrice && !isNaN(Number(assignCustomPrice))) {
        payload.customPrice = Number(assignCustomPrice);
      }
      const res = await superAdminService.bulkAssignModules(payload);
      if (res.status) {
        const msg = res.message || "Modules assigned successfully.";
        Swal.fire("Assigned!", msg, "success");
        resetAssignSection();
        fetchModuleAccess();
        fetchModuleAccessLogs();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAssignModuleLoading(false);
    }
  };

  // --- Bundle assignment ---
  const [showBundleAssignSection, setShowBundleAssignSection] = useState(false);
  const [bundleDropdownList, setBundleDropdownList] = useState<{ value: number; label: string; bundlePrice: number | null; pricingType: string; apps: { appId: number; appName: string | null }[] }[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);
  const [bundleValidFrom, setBundleValidFrom] = useState("");
  const [bundleValidTo, setBundleValidTo] = useState("");
  const [bundleCustomPrice, setBundleCustomPrice] = useState<string>("");
  const [assignBundleLoading, setAssignBundleLoading] = useState(false);
  const [assignedBundles, setAssignedBundles] = useState<any[]>([]);
  const [assignedBundlesLoading, setAssignedBundlesLoading] = useState(false);

  const fetchAssignedBundles = useCallback(async () => {
    if (!customerId) return;
    setAssignedBundlesLoading(true);
    try {
      const res = await superAdminService.getCustomerBundles(customerId);
      if (res.status && res.data) {
        setAssignedBundles(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      setAssignedBundles([]);
    } finally {
      setAssignedBundlesLoading(false);
    }
  }, [customerId]);

  const loadBundleDropdown = () => {
    if (bundleDropdownList.length === 0) {
      superAdminService.getBundleDropdown().then((res: any) => {
        if (res.status && res.data) {
          const list = Array.isArray(res.data) ? res.data : [];
          setBundleDropdownList(list.map((b: any) => ({
            value: b.bundleId,
            label: b.bundleName,
            bundlePrice: b.bundlePrice != null ? Number(b.bundlePrice) : null,
            pricingType: b.pricingType || "Flat",
            apps: (b.apps || []).map((a: any) => ({ appId: a.appId, appName: a.appName })),
          })));
        }
      });
    }
  };

  const handleAssignBundle = async () => {
    if (!selectedBundleId || !bundleValidFrom || !bundleValidTo) {
      Swal.fire("Warning", "Please select a bundle and date range.", "warning");
      return;
    }
    // Check for app conflicts — prevent if any bundle app is already individually assigned
    const selectedBundleData = bundleDropdownList.find((b) => b.value === selectedBundleId);
    const individuallyAssignedAppIds = Array.from(new Set(
      moduleAccess.filter((m) => m.isActive && m.appId).map((m) => m.appId)
    ));
    const conflictingApps = (selectedBundleData?.apps || []).filter(
      (a) => individuallyAssignedAppIds.includes(a.appId)
    );
    if (conflictingApps.length > 0) {
      const names = conflictingApps.map((a) => a.appName).filter(Boolean).join(", ");
      Swal.fire("Conflict", `These apps are already individually assigned: ${names}. Please unassign them first before assigning this bundle.`, "warning");
      return;
    }
    setAssignBundleLoading(true);
    try {
      const payload: any = {
        customerId,
        bundleId: selectedBundleId,
        validFrom: bundleValidFrom,
        validTo: bundleValidTo,
      };
      if (bundleCustomPrice && !isNaN(Number(bundleCustomPrice))) {
        payload.effectivePrice = Number(bundleCustomPrice);
      }
      const res = await superAdminService.assignBundleToCustomer(payload);
      if (res.status) {
        Swal.fire("Assigned!", res.message || "Bundle assigned successfully.", "success");
        setShowBundleAssignSection(false);
        setSelectedBundleId(null);
        setBundleValidFrom(billingConfig?.billingStartDate ? moment(billingConfig.billingStartDate).format("YYYY-MM-DD") : "");
        setBundleValidTo(billingConfig?.billingEndDate ? moment(billingConfig.billingEndDate).format("YYYY-MM-DD") : "");
        setBundleCustomPrice("");
        fetchAssignedBundles();
        fetchModuleAccess();
        fetchModuleAccessLogs();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAssignBundleLoading(false);
    }
  };

  const handleRevokeBundle = (bundle: any) => {
    Swal.fire({
      title: "Revoke Bundle?",
      text: `Remove "${bundle.bundleName || bundle.bundle?.bundleName}" from this customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, revoke",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setRevokingBundleId(bundle.customerBundleId);
        try {
          const res = await superAdminService.unassignBundleFromCustomer({
            customerBundleId: bundle.customerBundleId,
          });
          if (res.status) {
            Swal.fire("Revoked!", res.message || "Bundle revoked.", "success");
            fetchAssignedBundles();
            fetchModuleAccess();
            fetchModuleAccessLogs();
          } else {
            Swal.fire("Error", res.message, "error");
          }
        } catch (e: any) {
          Swal.fire("Error", e.message, "error");
        } finally {
          setRevokingBundleId(null);
        }
      }
    });
  };

  const handleRevokeModule = (record: ModuleAccessItem) => {
    Swal.fire({
      title: "Revoke Module Access?",
      text: `Remove access for "${record.moduleName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, revoke",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.revokeModuleAccess({
            customerModulePriceId: record.customerModulePriceId,
          });
          if (res.status) {
            Swal.fire("Revoked!", "Module access revoked.", "success");
            fetchModuleAccess();
          } else {
            Swal.fire("Error", res.message, "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  // --- Modify modules for an assigned app ---
  const handleOpenModifyModules = async (appId: number) => {
    if (modifyingAppId === appId) {
      setModifyingAppId(null);
      setModifyModulesList([]);
      return;
    }
    setModifyingAppId(appId);
    setModifyModulesLoading(true);
    try {
      const res = await superAdminService.getApplicationModules(appId);
      if (res.status && res.data) {
        const allModules = Array.isArray(res.data) ? res.data : [];
        const assignedForApp = moduleAccess.filter((m) => m.isActive && m.appId === appId);
        const assignedModuleIds = assignedForApp.map((m) => m.moduleId);
        setModifyModulesList(
          allModules.map((m: any) => ({
            moduleId: m.moduleId || m.moduleID,
            moduleName: m.moduleName,
            isAssigned: assignedModuleIds.includes(m.moduleId || m.moduleID),
          }))
        );
      }
    } catch (e) {
      setModifyModulesList([]);
    } finally {
      setModifyModulesLoading(false);
    }
  };

  const handleToggleModuleAccess = async (appId: number, moduleId: number, moduleName: string, currentlyAssigned: boolean) => {
    if (currentlyAssigned) {
      // Revoke this specific module
      const record = moduleAccess.find((m) => m.isActive && m.appId === appId && m.moduleId === moduleId);
      if (record) {
        const confirm = await Swal.fire({
          title: "Revoke Module?",
          text: `Remove access for "${moduleName}"?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, revoke",
        });
        if (!confirm.isConfirmed) return;
        try {
          const res = await superAdminService.revokeModuleAccess({ customerModulePriceId: record.customerModulePriceId });
          if (res.status) {
            Swal.fire("Revoked!", "Module access revoked.", "success");
            fetchModuleAccess();
            fetchModuleAccessLogs();
            // Update local list
            setModifyModulesList((prev) => prev.map((m) => m.moduleId === moduleId ? { ...m, isAssigned: false } : m));
          } else {
            Swal.fire("Error", res.message, "error");
          }
        } catch (e: any) {
          Swal.fire("Error", e.message, "error");
        }
      }
    } else {
      // Assign this specific module — need validFrom/validTo from existing app assignment
      const existingAppModule = moduleAccess.find((m) => m.isActive && m.appId === appId);
      if (!existingAppModule) {
        Swal.fire("Error", "Cannot determine assignment dates. Please re-assign the entire application.", "error");
        return;
      }
      try {
        const res = await superAdminService.bulkAssignModules({
          customerId,
          appId,
          moduleIds: [moduleId],
          validFrom: moment(existingAppModule.validFrom).format("YYYY-MM-DD"),
          validTo: moment(existingAppModule.validTo).format("YYYY-MM-DD"),
        });
        if (res.status) {
          Swal.fire("Assigned!", `Module "${moduleName}" assigned.`, "success");
          fetchModuleAccess();
          fetchModuleAccessLogs();
          setModifyModulesList((prev) => prev.map((m) => m.moduleId === moduleId ? { ...m, isAssigned: true } : m));
        } else {
          const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
          Swal.fire("Error", msg, "error");
        }
      } catch (e: any) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  const handleSaveAppEdit = async () => {
    if (!editingAppAccess) return;
    setEditAccessSaving(true);
    try {
      // Update all active modules for this app with new dates/price (bulk)
      const modulesForApp = moduleAccess.filter((m) => m.isActive && m.appId === editingAppAccess.appId);
      const customerModulePriceIds = modulesForApp.map((mod) => mod.customerModulePriceId);
      if (customerModulePriceIds.length > 0) {
        const payload: any = { customerModulePriceIds };
        if (editingAppAccess.validFrom) payload.validFrom = editingAppAccess.validFrom;
        if (editingAppAccess.validTo) payload.validTo = editingAppAccess.validTo;
        if (editingAppAccess.customPrice !== "") {
          payload.customPrice = Number(editingAppAccess.customPrice);
        }
        await superAdminService.bulkUpdateModuleAccess(payload);
      }
      Swal.fire("Updated!", "Application access updated successfully.", "success");
      setEditingAppAccess(null);
      fetchModuleAccess();
      fetchModuleAccessLogs();
    } catch (e: any) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setEditAccessSaving(false);
    }
  };

  const handleSaveBundleEdit = async () => {
    if (!editingBundleAccess) return;
    setEditAccessSaving(true);
    try {
      const payload: any = { customerBundleId: editingBundleAccess.customerBundleId };
      if (editingBundleAccess.validFrom) payload.validFrom = editingBundleAccess.validFrom;
      if (editingBundleAccess.validTo) payload.validTo = editingBundleAccess.validTo;
      if (editingBundleAccess.effectivePrice !== "") {
        payload.effectivePrice = Number(editingBundleAccess.effectivePrice);
      }
      const res = await superAdminService.updateCustomerBundle(payload);
      if (res.status) {
        Swal.fire("Updated!", "Bundle assignment updated successfully.", "success");
        setEditingBundleAccess(null);
        fetchAssignedBundles();
        fetchModuleAccessLogs();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (e: any) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setEditAccessSaving(false);
    }
  };

  const fetchModuleAccessLogs = useCallback(async () => {
    if (!customerId) return;
    setLogsLoading(true);
    try {
      const res = await superAdminService.getModuleAccessLogs(customerId);
      if (res.status && res.data) {
        setModuleAccessLogs(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      setModuleAccessLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [customerId]);

  // =============================================
  // User Management Limits
  // =============================================
  const fetchUserLimits = useCallback(async () => {
    setUserLimitsLoading(true);
    try {
      const res = await superAdminService.getUserLimits(customerId);
      if (res.status && res.data) setUserLimits(res.data);
    } catch (error: any) {
      console.error("Failed to fetch user limits:", error.message);
    } finally {
      setUserLimitsLoading(false);
    }
  }, [customerId]);

  const startUserLimitsEdit = () => {
    if (!userLimits) return;
    userLimitsForm.reset({
      userLimitType: userLimits.userLimitType || "Restricted",
      maxUsers: userLimits.maxUsers || 10,
      maxProjects: userLimits.maxProjects || 5,
      maxSuppliers: userLimits.maxSuppliers || 5,
      maxStorage: userLimits.maxStorage || "",
    });
    setUserLimitsEditMode(true);
  };

  const saveUserLimits = async (formData: any) => {
    setUserLimitsSaving(true);
    try {
      const payload: any = { customerId };
      const dirty = userLimitsForm.formState.dirtyFields;
      if (dirty.userLimitType) payload.userLimitType = formData.userLimitType;
      if (dirty.maxUsers) payload.maxUsers = Number(formData.maxUsers);
      if (dirty.maxProjects) payload.maxProjects = Number(formData.maxProjects);
      if (dirty.maxSuppliers) payload.maxSuppliers = Number(formData.maxSuppliers);
      if (dirty.maxStorage) payload.maxStorage = Number(formData.maxStorage);
      if (Object.keys(payload).length <= 1) {
        setUserLimitsEditMode(false);
        return;
      }
      const res = await superAdminService.updateUserLimits(payload);
      if (res.status) {
        Swal.fire("Updated!", "User limits updated.", "success");
        setUserLimitsEditMode(false);
        fetchUserLimits();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setUserLimitsSaving(false);
    }
  };

  // =============================================
  // Tax Configuration
  // =============================================
  const fetchTaxConfig = useCallback(async () => {
    setTaxLoading(true);
    try {
      const res = await superAdminService.getTaxConfig(customerId);
      if (res.status && res.data) {
        setTaxConfig(res.data);
        setSelectedTaxId(res.data.taxId || undefined);
      }
    } catch (error: any) {
      console.error("Failed to fetch tax config:", error.message);
    } finally {
      setTaxLoading(false);
    }
  }, [customerId]);

  const loadTaxOptions = () => {
    if (taxOptions.length === 0) {
      superAdminService.getTaxDropdown().then((res: any) => {
        if (res.status && res.data) {
          const list = Array.isArray(res.data) ? res.data : [];
          setTaxOptions(list.map((t: any) => ({ value: t.taxId, label: `${t.taxName} (${t.taxRate}%)` })));
        }
      });
    }
  };

  const saveTaxConfig = async () => {
    if (!selectedTaxId) return;
    setTaxSaving(true);
    try {
      const res = await superAdminService.updateTaxConfig({ customerId, taxId: selectedTaxId });
      if (res.status) {
        Swal.fire("Updated!", "Tax configuration updated.", "success");
        setTaxEditMode(false);
        fetchTaxConfig();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setTaxSaving(false);
    }
  };

  // =============================================
  // Access Control
  // =============================================
  const fetchAccessControl = useCallback(async () => {
    setAccessLoading(true);
    try {
      const [statusRes, logsRes] = await Promise.allSettled([
        superAdminService.getAccessControlStatus(customerId),
        superAdminService.getAccessControlLogs(customerId),
      ]);
      if (statusRes.status === "fulfilled" && statusRes.value.status && statusRes.value.data)
        setAccessStatus(statusRes.value.data);
      if (logsRes.status === "fulfilled" && logsRes.value.status && logsRes.value.data)
        setAccessLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
    } catch (error: any) {
      console.error("Failed to fetch access control:", error.message);
    } finally {
      setAccessLoading(false);
    }
  }, [customerId]);

  const handleHoldUnhold = async (action: "hold" | "unhold") => {
    setHoldLoading(true);
    try {
      const payload: any = { customerId };
      if (holdReason.trim()) payload.reason = holdReason.trim();
      const res =
        action === "hold"
          ? await superAdminService.holdCustomerAccess(payload)
          : await superAdminService.unholdCustomerAccess(payload);
      if (res.status) {
        Swal.fire(
          action === "hold" ? "Held!" : "Unhold!",
          `Customer ${action === "hold" ? "put on hold" : "removed from hold"} successfully.`,
          "success"
        );
        setHoldModal(false);
        setHoldReason("");
        fetchAccessControl();
        fetchCustomer(false);
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setHoldLoading(false);
    }
  };

  const saveAutoHoldConfig = async () => {
    if (autoHoldEnabled && !autoHoldDays) {
      Swal.fire("Warning", "Please select the overdue days threshold.", "warning");
      return;
    }
    setAutoHoldSaving(true);
    try {
      const res = await superAdminService.updateBillingConfig({
        customerId,
        autoHoldEnabled,
        autoHoldDays: autoHoldEnabled ? autoHoldDays : null,
      });
      if (res.status) {
        Swal.fire("Updated!", "Auto-hold configuration saved.", "success");
        fetchBillingConfig();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAutoHoldSaving(false);
    }
  };

  // =============================================
  // Invoice Report
  // =============================================
  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await superAdminService.getInvoiceList({
        searchQuery: "",
        pageSize: 100,
        direction: "P",
        pageDiff: 0,
        "filters[customerId]": customerId,
      });
      if (res.status && res.data) {
        const list = Array.isArray(res.data) ? res.data : res.data?.list || [];
        setInvoices(list);
        // Build summary: totalAmount now includes advance in the stored value
        const totalAmount = list.reduce((sum: number, inv: any) => sum + (Number(inv.totalAmount) || 0), 0);
        const paidAmount = list.reduce((sum: number, inv: any) => sum + (Number(inv.paidAmount) || 0), 0);
        const pendingAmount = totalAmount - paidAmount;
        const paidCount = list.filter((inv: any) => inv.paymentStatus === "Fully Paid").length;
        const pendingCount = list.filter((inv: any) => ["Unpaid", "Partially Paid", "Overdue"].includes(inv.paymentStatus)).length;
        setInvoiceSummary({ totalAmount, paidAmount, pendingAmount, total: list.length, paidCount, pendingCount });
      }
    } catch (error: any) {
      console.error("Failed to fetch invoices:", error.message);
    } finally {
      setInvoicesLoading(false);
    }
  }, [customerId]);

  // =============================================
  // Create Manual Invoice
  // =============================================
  const openCreateInvoiceModal = async () => {
    // Ensure required data is loaded
    if (moduleAccess.length === 0) {
      setModulesLoading(true);
      try {
        const res = await superAdminService.getModuleAccess(customerId);
        if (res.status && res.data) setModuleAccess(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* ignore */ }
      setModulesLoading(false);
    }
    if (!taxConfig) {
      try {
        const res = await superAdminService.getTaxConfig(customerId);
        if (res.status && res.data) {
          setTaxConfig(res.data);
          setSelectedTaxId(res.data.taxId || undefined);
        }
      } catch (e) { /* ignore */ }
    }
    if (!billingConfig) {
      try {
        const res = await superAdminService.getBillingConfig(customerId);
        if (res.status && res.data) setBillingConfig(res.data);
      } catch (e) { /* ignore */ }
    }
    // Fetch customer bundles & advance balance
    try {
      const bundleRes = await superAdminService.getCustomerBundles(customerId);
      if (bundleRes.status && bundleRes.data) setCustomerBundles(Array.isArray(bundleRes.data) ? bundleRes.data : []);
    } catch (e) { /* ignore */ }
    try {
      const recRes = await superAdminService.getCustomerReceivableDetail(customerId);
      if (recRes.status && recRes.data) {
        setInvoiceAdvanceBalance(Number(recRes.data.summary?.advanceBalance) || 0);
      }
    } catch (e) { /* ignore */ }
    // Reset form
    setSelectedInvoiceAppIds([]);
    setSelectedInvoiceBundleIds([]);
    setInvoiceBillingPeriod("");
    setInvoiceDiscountType(undefined);
    setInvoiceDiscountValue(undefined);
    setInvoiceDiscountReason("");
    setInvoiceNotes("");
    setInvoiceUseAdvance(false);
    setInvoiceAdvanceToAdjust(undefined);
    setShowCreateInvoice(true);
  };

  const closeCreateInvoiceSection = () => {
    setShowCreateInvoice(false);
    setSelectedInvoiceAppIds([]);
    setSelectedInvoiceBundleIds([]);
    setInvoiceBillingPeriod("");
    setInvoiceDiscountType(undefined);
    setInvoiceDiscountValue(undefined);
    setInvoiceDiscountReason("");
    setInvoiceNotes("");
    setInvoiceUseAdvance(false);
    setInvoiceAdvanceToAdjust(undefined);
  };

  const handleCreateInvoice = async () => {
    if (selectedInvoiceAppIds.length === 0 && selectedInvoiceBundleIds.length === 0) {
      Swal.fire("Warning", "Please select at least one application or bundle to invoice.", "warning");
      return;
    }
    setCreateInvoiceLoading(true);
    try {
      const payload: any = {
        customerId,
        appIds: selectedInvoiceAppIds,
      };
      if (selectedInvoiceBundleIds.length > 0) payload.bundleIds = selectedInvoiceBundleIds;
      if (invoiceBillingPeriod.trim()) payload.billingPeriod = invoiceBillingPeriod.trim();
      if (invoiceDiscountType && invoiceDiscountValue && invoiceDiscountValue > 0) {
        payload.manualDiscountType = invoiceDiscountType;
        payload.manualDiscountValue = invoiceDiscountValue;
        if (invoiceDiscountReason.trim()) payload.discountReason = invoiceDiscountReason.trim();
      }
      if (invoiceNotes.trim()) payload.notes = invoiceNotes.trim();
      if (invoiceUseAdvance && invoiceAdvanceToAdjust && invoiceAdvanceToAdjust > 0) {
        payload.advanceToAdjust = invoiceAdvanceToAdjust;
      }

      const res = await superAdminService.createManualInvoice(payload);
      if (res.status) {
        Swal.fire("Success!", "Invoice created successfully.", "success");
        closeCreateInvoiceSection();
        fetchInvoices();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg || "Failed to create invoice.", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setCreateInvoiceLoading(false);
    }
  };

  // =============================================
  // Payment Report
  // =============================================
  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const res = await superAdminService.getCustomerReceivableDetail(customerId);
      if (res.status && res.data) {
        setReceivableDetail(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch payments:", error.message);
    } finally {
      setPaymentsLoading(false);
    }
  }, [customerId]);

  const resetAdvanceForm = () => {
    setAdvFormAmount(null);
    setAdvFormDate("");
    setAdvFormMethod(undefined);
    setAdvFormRefNo("");
    setAdvFormBank("");
    setAdvFormRemarks("");
  };

  const handleRecordAdvance = async () => {
    if (!advFormAmount || advFormAmount <= 0) {
      Swal.fire("Warning", "Please enter a valid amount.", "warning");
      return;
    }
    if (!advFormDate) {
      Swal.fire("Warning", "Please select a payment date.", "warning");
      return;
    }
    setAdvFormSaving(true);
    try {
      const payload: any = {
        customerId,
        amount: advFormAmount,
        paymentDate: advFormDate,
        method: advFormMethod || "BankTransfer",
        referenceNumber: advFormRefNo.trim() || `ADV-${Date.now()}`,
      };
      if (advFormBank.trim()) payload.bankName = advFormBank.trim();
      if (advFormRemarks.trim()) payload.remarks = advFormRemarks.trim();

      const res = await superAdminService.recordAdvancePayment(payload);
      if (res.status) {
        Swal.fire("Recorded!", "Advance payment recorded successfully.", "success");
        setShowAdvanceForm(false);
        resetAdvanceForm();
        fetchPayments();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAdvFormSaving(false);
    }
  };

  const startEditAdvance = (record: any) => {
    setEditingAdvanceId(record.advancePaymentId);
    setEditAdvData({
      amount: Number(record.advanceAmount),
      paymentDate: record.advanceDate ? moment(record.advanceDate).format("YYYY-MM-DD") : "",
      method: record.paymentMethod,
      referenceNumber: record.referenceNumber || "",
      bankName: record.bankName || "",
      remarks: record.remarks || "",
    });
  };

  const handleUpdateAdvance = async () => {
    if (!editingAdvanceId) return;
    setAdvFormSaving(true);
    try {
      const payload: any = { advancePaymentId: editingAdvanceId };
      if (editAdvData.amount) payload.amount = editAdvData.amount;
      if (editAdvData.paymentDate) payload.paymentDate = editAdvData.paymentDate;
      if (editAdvData.method) payload.method = editAdvData.method;
      if (editAdvData.referenceNumber !== undefined) payload.referenceNumber = editAdvData.referenceNumber;
      if (editAdvData.bankName !== undefined) payload.bankName = editAdvData.bankName;
      if (editAdvData.remarks !== undefined) payload.remarks = editAdvData.remarks;

      const res = await superAdminService.updateAdvancePayment(payload);
      if (res.status) {
        Swal.fire("Updated!", "Advance payment updated successfully.", "success");
        setEditingAdvanceId(null);
        setEditAdvData({});
        fetchPayments();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAdvFormSaving(false);
    }
  };

  const handleCancelAdvance = async (record: any) => {
    const result = await Swal.fire({
      title: "Cancel Advance Payment?",
      text: `Are you sure you want to cancel ${record.advanceReferenceNo}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No",
      input: "text",
      inputLabel: "Reason (optional)",
      inputPlaceholder: "Enter cancellation reason",
    });

    if (result.isConfirmed) {
      try {
        const res = await superAdminService.cancelAdvancePayment({
          advancePaymentId: record.advancePaymentId,
          reason: result.value || undefined,
        });
        if (res.status) {
          Swal.fire("Cancelled!", "Advance payment cancelled.", "success");
          fetchPayments();
        } else {
          const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
          Swal.fire("Error", msg, "error");
        }
      } catch (error: any) {
        Swal.fire("Error", error.message, "error");
      }
    }
  };

  // =============================================
  // Coupon Link/Unlink
  // =============================================
  const loadCouponDropdown = useCallback(async () => {
    try {
      const res = await superAdminService.getCouponDropdown(customerId);
      if (res.status && res.data) {
        const options = (Array.isArray(res.data) ? res.data : []).map((c: any) => ({
          value: c.couponId || c.id,
          label: `${c.couponCode} — ${c.couponName || c.name}`,
        }));
        setCouponDropdown(options);
      }
    } catch (error: any) {
      console.error("Failed to load coupon dropdown:", error.message);
    }
  }, []);

  const fetchCouponHistory = useCallback(async () => {
    setCouponHistoryLoading(true);
    try {
      const res = await superAdminService.getCustomerCouponHistory(customerId);
      if (res.status) {
        setCouponHistory(res.data || []);
      }
    } catch (error: any) {
      console.error("Failed to load coupon history:", error.message);
    } finally {
      setCouponHistoryLoading(false);
    }
  }, [customerId]);

  const handleLinkCoupon = async () => {
    if (!selectedCouponId) return;
    setLinkCouponLoading(true);
    try {
      const payload: any = { customerId, couponId: selectedCouponId };
      if (couponReferenceFrom.trim()) payload.referenceFrom = couponReferenceFrom.trim();
      const res = await superAdminService.linkCouponToCustomer(payload);
      if (res.status) {
        Swal.fire("Success", "Coupon linked successfully", "success");
        setLinkCouponModal(false);
        setSelectedCouponId(undefined);
        setCouponReferenceFrom("");
        fetchCustomer(false); // refresh linkedCoupon without page loader
        fetchCouponHistory();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setLinkCouponLoading(false);
    }
  };

  const handleUnlinkCoupon = async () => {
    if (!linkedCoupon) return;
    const result = await Swal.fire({
      title: "Unlink Coupon?",
      text: `Are you sure you want to unlink "${linkedCoupon.couponCode}" from this customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, unlink it",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.unlinkCouponFromCustomer({
        customerId,
        couponId: linkedCoupon.couponId,
      });
      if (res.status) {
        Swal.fire("Unlinked!", "Coupon unlinked successfully", "success");
        setLinkedCoupon(null);
        fetchCustomer(false);
        fetchCouponHistory();
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // =============================================
  // RENDER
  // =============================================
  if (pageLoading) {
    const loader = (
      <LoadingSpinner height={embedded ? "200px" : "60vh"} tip="Loading control panel..." />
    );
    if (embedded) return loader;
    return (
      <React.Fragment>
        <Head title="Customer Control Panel" />
        <Content>{loader}</Content>
      </React.Fragment>
    );
  }

  if (!customer) {
    const notFound = (
      <Flex align="center" justify="center" style={{ height: embedded ? "200px" : "40vh" }}>
        <div style={{ textAlign: "center" }}>
          <Empty description="Customer not found" />
          {!embedded && (
            <Button
              color="primary"
              className="mt-3"
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          )}
        </div>
      </Flex>
    );
    if (embedded) return notFound;
    return (
      <React.Fragment>
        <Head title="Customer Control Panel" />
        <Content>
          {notFound}
        </Content>
      </React.Fragment>
    );
  }

  const isOnHold = customer.billingStatus === "Hold";
  const isDraft = customer.billingStatus === "Draft";
  const isActive = customer.billingStatus === "Active";

  // =============================================
  // Tab content renderers
  // =============================================

  // --- Tab 1: Billing ---
  const renderBilling = () => {
    if (billingLoading)
      return <LoadingSpinner />;
    if (!billingConfig)
      return <Empty description="No billing configuration found" />;

    if (billingEditMode) {
      return (
        <FormProvider {...billingForm}>
          <form onSubmit={billingForm.handleSubmit(saveBillingConfig)}>
            <Row className="gy-3">
              <Col lg="3"><FormSelect name="billingMode" label="Billing Mode" options={billingModeOptions} required placeholder="-Select-" /></Col>
              <Col lg="3"><FormSelect name="billingFrequency" label="Billing Frequency" options={billingFrequencyOptions} required placeholder="-Select-" /></Col>
              <Col lg="3"><FormDatePicker name="billingStartDate" label="Start Date" placeholder="Start date" /></Col>
              <Col lg="3"><FormDatePicker name="billingEndDate" label="End Date" placeholder="End date" /></Col>
              <Col lg="3">
                <FormSelect
                  name="invoiceAutoTriggerDate"
                  label="Auto Trigger Date"
                  options={Array.from({ length: 26 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}${i + 1 === 1 ? "st" : i + 1 === 2 ? "nd" : i + 1 === 3 ? "rd" : "th"} of every month` }))}
                  placeholder="Select date (1-26)"
                />
              </Col>
              <Col lg="3"><FormInput name="paymentDueDays" label="Payment Due Days" type="number" placeholder="1-90" /></Col>
              <Col lg="3">
                <FormSelect name="advancePaymentAllowed" label="Advance Payment" options={[
                  { value: "true", label: "Allowed" },
                  { value: "false", label: "Not Allowed" },
                ]} placeholder="-Select-" />
              </Col>
            </Row>
            <div className="mt-4" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" type="submit" disabled={billingSaving}>
                {billingSaving ? <LoadingSpinner size="sm" inline /> : "Save Changes"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setBillingEditMode(false)} disabled={billingSaving}>
                Cancel
              </Button>
            </div>
          </form>
        </FormProvider>
      );
    }

    const ordSuffix = (n: number) => n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";

    const field = (label: string, value: React.ReactNode) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#364a63" }}>{value}</span>
      </div>
    );

    return (
      <>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Button color="primary" size="sm" className="btn-dim" onClick={startBillingEdit}>
            <Icon name="edit" /> Edit Configuration
          </Button>
        </div>

        {/* Billing config panel */}
        <div style={{ background: "#f8f9fc", border: "1px solid #e9ecef", borderRadius: 8, padding: "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px 32px" }}>
            {field("Billing Mode",
              <Tag color={billingConfig.billingMode === "Auto" ? "green" : "orange"} style={{ fontSize: 12 }}>
                {billingConfig.billingMode}
              </Tag>
            )}
            {field("Billing Frequency", billingConfig.billingFrequency || <span style={{ color: "#b0b0b0" }}>—</span>)}
            {field("Start Date",
              billingConfig.billingStartDate
                ? moment(billingConfig.billingStartDate).format("DD MMM YYYY")
                : <span style={{ color: "#b0b0b0" }}>Not Set</span>
            )}
            {field("End Date",
              billingConfig.billingEndDate
                ? moment(billingConfig.billingEndDate).format("DD MMM YYYY")
                : <span style={{ color: "#b0b0b0" }}>Not Set</span>
            )}
            {field("Invoice Auto Trigger",
              billingConfig.invoiceAutoTriggerDate
                ? <span>{billingConfig.invoiceAutoTriggerDate}<sup style={{ fontSize: 10 }}>{ordSuffix(billingConfig.invoiceAutoTriggerDate)}</sup><span style={{ fontWeight: 400, color: "#8091a7", marginLeft: 4, fontSize: 12 }}>of every month</span></span>
                : <span style={{ color: "#b0b0b0" }}>Not Set</span>
            )}
            {field("Payment Due",
              billingConfig.paymentDueDays
                ? <span>{billingConfig.paymentDueDays}<span style={{ fontWeight: 400, color: "#8091a7", marginLeft: 4, fontSize: 12 }}>days after invoice</span></span>
                : <span style={{ color: "#b0b0b0" }}>Not Set</span>
            )}
            {field("Advance Payment",
              <Tag color={billingConfig.advancePaymentAllowed ? "green" : "default"} style={{ fontSize: 12 }}>
                {billingConfig.advancePaymentAllowed ? "Allowed" : "Not Allowed"}
              </Tag>
            )}
          </div>
        </div>

        <Divider orientation="left" style={{ marginTop: 28 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}><CalculatorOutlined style={{ marginRight: 6 }} />Tax Configuration</span>
        </Divider>
        {renderTax()}
      </>
    );
  };

  // --- Tab 2: Application Modules ---
  const renderModules = () => {
    // Collect appIds covered by assigned bundles — used for disabling in assign dropdowns
    const bundleCoveredAppIds = new Set<number>();
    assignedBundles.forEach((ab: any) => {
      (ab.apps || []).forEach((app: any) => {
        if (app.appId) bundleCoveredAppIds.add(app.appId);
      });
    });

    // Group moduleAccess by appName — show only individually assigned active apps (exclude bundle-assigned and inactive)
    const groupedByApp: Record<string, ModuleAccessItem[]> = {};
    moduleAccess.forEach((item) => {
      if (item.customerBundleId) return; // Skip bundle-assigned modules
      if (!item.isActive) return; // Skip inactive/revoked modules
      const appKey = item.appName || "Unassigned";
      if (!groupedByApp[appKey]) groupedByApp[appKey] = [];
      groupedByApp[appKey].push(item);
    });

    // All individually assigned app IDs (exclude bundle-assigned, for dropdowns & conflict detection)
    const assignedAppIds = Array.from(new Set(
      moduleAccess.filter((m) => m.isActive && m.appId && !m.customerBundleId).map((m) => m.appId)
    ));

    return (
      <>
        {/* ── Application Assignment ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span className="text-soft">
            {moduleAccess.filter((m) => m.isActive).length} active application module(s)
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {!showAssignSection && (
              <Button color="primary" size="sm" onClick={() => setShowAssignSection(true)}>
                <Icon name="plus" /> Assign Application
              </Button>
            )}
            {!showBundleAssignSection && (
              <Button color="warning" size="sm" onClick={() => { setShowBundleAssignSection(true); loadBundleDropdown(); }}>
                <GiftOutlined style={{ marginRight: 4 }} /> Assign Bundle
              </Button>
            )}
          </div>
        </div>

        {/* Inline App Assign Section */}
        {showAssignSection && (() => {
          const selectedRecord = pricingRecords.find((p: any) => p.modulePriceId === selectedPricingId);
          return (
            <AntCard
              size="small"
              style={{ marginBottom: 16, border: "1px solid #e5e9f2", background: "#fafbfc" }}
              bodyStyle={{ padding: "16px 20px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h6 style={{ margin: 0, fontWeight: 600 }}>
                  <AppstoreOutlined style={{ marginRight: 8, color: "#6576ff" }} />
                  Assign Application (All Modules)
                </h6>
                <Button size="sm" color="secondary" className="btn-icon btn-dim" onClick={resetAssignSection}>
                  <Icon name="cross" />
                </Button>
              </div>
              <Row className="gy-3">
                <Col lg="5">
                  <label className="form-label">Select Pricing Plan <span className="mandatory">*</span></label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select pricing plan"
                    showSearch
                    filterOption={(input: string, option: any) => {
                      const label = typeof option?.label === 'string' ? option.label : '';
                      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    optionLabelProp="label"
                    options={pricingRecords.map((p: any) => {
                      const inBundle = bundleCoveredAppIds.has(p.appId);
                      const alreadyAssigned = assignedAppIds.includes(p.appId);
                      const region = [p.countryName, p.stateName].filter(Boolean).join(" > ");
                      const label = `${p.pricingName || p.appName} — ${p.appName} (₹${Number(p.unitPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}, ${p.pricingType}${region ? ", " + region : ""})`;
                      return {
                        value: p.modulePriceId,
                        label,
                        disabled: inBundle || alreadyAssigned,
                        title: inBundle ? "Already in a bundle" : alreadyAssigned ? "Already assigned" : "",
                      };
                    })}
                    value={selectedPricingId || undefined}
                    onChange={(val: number) => handlePricingChange(val)}
                  />
                  {pricingRecords.length === 0 && (
                    <div style={{ fontSize: 12, color: "#ff4d4f", marginTop: 4 }}>No active pricing plans found.</div>
                  )}
                </Col>
                <Col lg="2">
                  <label className="form-label">Plan Price</label>
                  <Input
                    style={{ width: "100%" }}
                    value={selectedRecord ? Number(selectedRecord.unitPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-"}
                    disabled
                    prefix="₹"
                  />
                </Col>
                <Col lg="2">
                  <label className="form-label">Custom Price</label>
                  <Input
                    style={{ width: "100%" }}
                    placeholder="Override"
                    type="number"
                    value={assignCustomPrice}
                    onChange={(e) => setAssignCustomPrice(e.target.value)}
                    prefix="₹"
                  />
                </Col>
                <Col lg="3">
                  <Row className="gy-2">
                    <Col lg="12">
                      <label className="form-label">Valid From <span className="mandatory">*</span></label>
                      <DatePicker
                        style={{ width: "100%" }}
                        placeholder="Start date"
                        format="DD MMM YYYY"
                        value={assignValidFrom ? dayjs(assignValidFrom) : null}
                        onChange={(date: any) => setAssignValidFrom(date ? date.format("YYYY-MM-DD") : "")}
                      />
                    </Col>
                    <Col lg="12">
                      <label className="form-label">Valid To <span className="mandatory">*</span></label>
                      <DatePicker
                        style={{ width: "100%" }}
                        placeholder="End date"
                        format="DD MMM YYYY"
                        value={assignValidTo ? dayjs(assignValidTo) : null}
                        onChange={(date: any) => setAssignValidTo(date ? date.format("YYYY-MM-DD") : "")}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {selectedRecord && (
                <div style={{ marginTop: 12, padding: "8px 12px", background: "#e6f7ff", borderRadius: 4, fontSize: 13 }}>
                  <CheckCircleOutlined style={{ color: "#1890ff", marginRight: 6 }} />
                  <strong>{selectedRecord.pricingName || selectedRecord.appName}</strong> — {selectedRecord.appName}
                  {" | "}{selectedRecord.pricingType} | {selectedRecord.billingFrequency}
                  {selectedRecord.countryName && ` | ${selectedRecord.countryName}${selectedRecord.stateName ? " > " + selectedRecord.stateName : ""}`}
                  {" | "}Price: ₹{Number(selectedRecord.unitPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  {assignCustomPrice ? ` → Custom: ₹${Number(assignCustomPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : ""}
                </div>
              )}

              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <Button
                  color="primary"
                  size="sm"
                  disabled={assignModuleLoading || !selectedPricingId || !assignValidFrom || !assignValidTo}
                  onClick={handleBulkAssign}
                >
                  {assignModuleLoading ? <LoadingSpinner size="sm" inline /> : "Assign All Modules"}
                </Button>
                <Button color="secondary" size="sm" onClick={resetAssignSection} disabled={assignModuleLoading}>
                  Cancel
                </Button>
              </div>
            </AntCard>
          );
        })()}

        {/* Inline Bundle Assign Section */}
        {showBundleAssignSection && (
          <AntCard
            size="small"
            style={{ marginBottom: 16, border: "1px solid #fff3cd", background: "#fffbe6" }}
            bodyStyle={{ padding: "16px 20px" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h6 style={{ margin: 0, fontWeight: 600 }}>
                <GiftOutlined style={{ marginRight: 8, color: "#faad14" }} />
                Assign Bundle
              </h6>
              <Button size="sm" color="secondary" className="btn-icon btn-dim" onClick={() => { setShowBundleAssignSection(false); setSelectedBundleId(null); setBundleValidFrom(""); setBundleValidTo(""); setBundleCustomPrice(""); }}>
                <Icon name="cross" />
              </Button>
            </div>
            {(() => {
              const selectedBundleInfo = bundleDropdownList.find((b) => b.value === selectedBundleId);
              return (
                <>
                  <Row className="gy-3">
                    <Col lg="4">
                      <label className="form-label">Bundle <span className="mandatory">*</span></label>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Select bundle"
                        showSearch
                        filterOption={(input: string, option: any) => {
                          const label = typeof option?.label === 'string' ? option.label : '';
                          return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        optionLabelProp="label"
                        options={bundleDropdownList.map((b) => {
                          const alreadyAssigned = assignedBundles.some((ab: any) => ab.bundleId === b.value);
                          const hasAppConflict = b.apps.some((a) => assignedAppIds.includes(a.appId));
                          const priceStr = b.bundlePrice != null ? `₹${Number(b.bundlePrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "N/A";
                          const appNames = b.apps.map((a) => a.appName).filter(Boolean).join(", ");
                          const label = `${b.label} — ${priceStr} (${b.apps.length} app${b.apps.length !== 1 ? "s" : ""}${appNames ? ": " + appNames : ""})`;
                          return {
                            value: b.value,
                            label: alreadyAssigned ? `${label} [already assigned]` : hasAppConflict ? `${label} [app conflict]` : label,
                            disabled: alreadyAssigned || hasAppConflict,
                          };
                        })}
                        value={selectedBundleId || undefined}
                        onChange={(val: number) => setSelectedBundleId(val)}
                      />
                    </Col>
                    <Col lg="2">
                      <label className="form-label">Bundle Price</label>
                      <Input
                        style={{ width: "100%" }}
                        value={selectedBundleInfo?.bundlePrice != null ? Number(selectedBundleInfo.bundlePrice).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-"}
                        disabled
                        prefix="₹"
                      />
                    </Col>
                    <Col lg="2">
                      <label className="form-label">Custom Price</label>
                      <Input
                        style={{ width: "100%" }}
                        placeholder="Override"
                        type="number"
                        value={bundleCustomPrice}
                        onChange={(e) => setBundleCustomPrice(e.target.value)}
                        prefix="₹"
                      />
                    </Col>
                    <Col lg="4">
                      <Row className="gy-2">
                        <Col lg="12">
                          <label className="form-label">Valid From <span className="mandatory">*</span></label>
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Start date"
                            format="DD MMM YYYY"
                            value={bundleValidFrom ? dayjs(bundleValidFrom) : null}
                            onChange={(date: any) => setBundleValidFrom(date ? date.format("YYYY-MM-DD") : "")}
                          />
                        </Col>
                        <Col lg="12">
                          <label className="form-label">Valid To <span className="mandatory">*</span></label>
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="End date"
                            format="DD MMM YYYY"
                            value={bundleValidTo ? dayjs(bundleValidTo) : null}
                            onChange={(date: any) => setBundleValidTo(date ? date.format("YYYY-MM-DD") : "")}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {selectedBundleInfo && (
                    <>
                      <div style={{ marginTop: 12, padding: "8px 12px", background: "#fff7e6", borderRadius: 4, fontSize: 13, border: "1px solid #ffe58f" }}>
                        <GiftOutlined style={{ color: "#faad14", marginRight: 6 }} />
                        <strong>{selectedBundleInfo.label}</strong> — {selectedBundleInfo.pricingType}
                        {" | "}Price: {selectedBundleInfo.bundlePrice != null ? `₹${Number(selectedBundleInfo.bundlePrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "N/A"}
                        {bundleCustomPrice ? ` → Custom: ₹${Number(bundleCustomPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : ""}
                      </div>
                      {selectedBundleInfo.apps.length > 0 && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#fafafa", borderRadius: 4, border: "1px solid #e8e8e8" }}>
                          <span style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, display: "block" }}>Apps in this bundle:</span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {selectedBundleInfo.apps.map((a) => {
                              const isConflict = assignedAppIds.includes(a.appId);
                              return (
                                <Tag key={a.appId} color={isConflict ? "red" : "blue"} style={{ fontSize: 12 }}>
                                  <AppstoreOutlined style={{ marginRight: 4 }} />
                                  {a.appName || `App ${a.appId}`}
                                  {isConflict && " (already assigned)"}
                                </Tag>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <Button
                color="warning"
                size="sm"
                disabled={assignBundleLoading || !selectedBundleId || !bundleValidFrom || !bundleValidTo}
                onClick={handleAssignBundle}
              >
                {assignBundleLoading ? <LoadingSpinner size="sm" inline /> : "Assign Bundle"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => { setShowBundleAssignSection(false); setSelectedBundleId(null); setBundleValidFrom(""); setBundleValidTo(""); setBundleCustomPrice(""); }} disabled={assignBundleLoading}>
                Cancel
              </Button>
            </div>
          </AntCard>
        )}

        {/* Assigned Applications — Grouped by App */}
        <h6 style={{ fontWeight: 600, marginBottom: 12, marginTop: 8 }}>
          <AppstoreOutlined style={{ marginRight: 6, color: "#6576ff" }} />
          Assigned Applications
        </h6>
        {modulesLoading ? (
          <LoadingSpinner />
        ) : Object.keys(groupedByApp).length > 0 ? (
          <Collapse
            defaultActiveKey={Object.keys(groupedByApp)}
            style={{ background: "transparent", border: "none" }}
            items={Object.entries(groupedByApp).map(([appName, items]) => {
              const firstItem = items[0];
              const activeCount = items.filter((i) => i.isActive).length;
              const hasActive = activeCount > 0;
              const appPrice = Number(firstItem.finalPrice) || Number(firstItem.appPrice) || 0;
              return {
                key: appName,
                label: (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <AppstoreOutlined style={{ fontSize: 16, color: "#6576ff" }} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{appName}</span>
                      <Tag color={hasActive ? "success" : "error"} style={{ marginLeft: 4 }}>
                        {hasActive ? `${activeCount} Active` : "Inactive"}
                      </Tag>
                      {appPrice > 0 && (
                        <Tag color="blue" style={{ marginLeft: 2 }}>
                          <span style={{ marginRight: 3 }}>₹</span>
                          {appPrice.toFixed(2)} {firstItem.currencyCode || ""}
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#8094ae" }}>
                      <span>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {firstItem.validFrom ? moment(firstItem.validFrom).format("DD MMM YYYY") : "-"}
                        {" — "}
                        {firstItem.validTo ? moment(firstItem.validTo).format("DD MMM YYYY") : "-"}
                      </span>
                      {hasActive && (
                        <>
                          <Tooltip title="Edit dates & price">
                            <EditOutlined
                              style={{ cursor: "pointer", color: "#6576ff", fontSize: 13 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAppAccess({
                                  appId: firstItem.appId!,
                                  validFrom: firstItem.validFrom ? moment(firstItem.validFrom).format("YYYY-MM-DD") : "",
                                  validTo: firstItem.validTo ? moment(firstItem.validTo).format("YYYY-MM-DD") : "",
                                  customPrice: firstItem.customPrice ? String(Number(firstItem.customPrice)) : "",
                                });
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={`Revoke all modules for ${appName}`}>
                            {revokingAppId === firstItem.appId ? (
                              <LoadingOutlined style={{ color: "#ff4d4f", fontSize: 13 }} />
                            ) : (
                            <DeleteOutlined
                              style={{ cursor: "pointer", color: "#ff4d4f", fontSize: 13 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (revokingAppId) return;
                                Swal.fire({
                                  title: "Revoke Entire Application?",
                                  text: `Remove all ${activeCount} module(s) for "${appName}"?`,
                                  icon: "warning",
                                  showCancelButton: true,
                                  confirmButtonText: "Yes, revoke all",
                                }).then(async (result) => {
                                  if (result.isConfirmed) {
                                    setRevokingAppId(firstItem.appId!);
                                    try {
                                      const activeItems = items.filter((i) => i.isActive);
                                      const ids = activeItems.map((mod) => mod.customerModulePriceId);
                                      const res = await superAdminService.bulkRevokeModuleAccess({
                                        customerModulePriceIds: ids,
                                      });
                                      if (res.status) {
                                        Swal.fire("Revoked!", `All modules for "${appName}" have been revoked.`, "success");
                                      } else {
                                        Swal.fire("Error", res.message || "Failed to revoke modules", "error");
                                      }
                                      fetchModuleAccess();
                                      fetchModuleAccessLogs();
                                    } catch (e: any) {
                                      Swal.fire("Error", e.message || "Failed to revoke modules", "error");
                                    } finally {
                                      setRevokingAppId(null);
                                    }
                                  }
                                });
                              }}
                            />
                            )}
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                ),
                children: (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {items.map((mod) => (
                          <Tag
                            key={mod.customerModulePriceId}
                            color={mod.isActive ? "blue" : "default"}
                            style={{ padding: "4px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                          >
                            {mod.moduleName}
                          </Tag>
                        ))}
                      </div>
                      {hasActive && firstItem.appId && (
                        <Button
                          size="sm"
                          color={modifyingAppId === firstItem.appId ? "secondary" : "primary"}
                          className="btn-dim"
                          style={{ whiteSpace: "nowrap", marginLeft: 12 }}
                          onClick={() => handleOpenModifyModules(firstItem.appId!)}
                        >
                          <EditOutlined style={{ marginRight: 4 }} />
                          {modifyingAppId === firstItem.appId ? "Close" : "Modify Modules"}
                        </Button>
                      )}
                    </div>
                    {/* Modify Modules Panel */}
                    {modifyingAppId === firstItem.appId && (
                      <div style={{ marginTop: 8, padding: "12px 16px", background: "#f0f5ff", borderRadius: 6, border: "1px solid #d6e4ff" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                          Toggle Module Access
                        </div>
                        {modifyModulesLoading ? (
                          <LoadingSpinner size="sm" inline />
                        ) : modifyModulesList.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
                            {modifyModulesList.map((mod) => (
                              <Checkbox
                                key={mod.moduleId}
                                checked={mod.isAssigned}
                                onChange={() => handleToggleModuleAccess(firstItem.appId!, mod.moduleId, mod.moduleName, mod.isAssigned)}
                              >
                                {mod.moduleName}
                              </Checkbox>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: "#8094ae", fontSize: 13 }}>No modules found for this application.</span>
                        )}
                      </div>
                    )}
                    {/* Inline Edit Form for App */}
                    {editingAppAccess && editingAppAccess.appId === firstItem.appId && (
                      <div style={{ marginTop: 8, padding: "12px 16px", background: "#fffbe6", borderRadius: 6, border: "1px solid #ffe58f" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Edit Date Range & Price</div>
                        <Row className="gy-2">
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Valid From</label>
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD MMM YYYY"
                              value={editingAppAccess.validFrom ? dayjs(editingAppAccess.validFrom) : null}
                              onChange={(date: any) => setEditingAppAccess((prev) => prev ? { ...prev, validFrom: date ? date.format("YYYY-MM-DD") : "" } : null)}
                            />
                          </Col>
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Valid To</label>
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD MMM YYYY"
                              value={editingAppAccess.validTo ? dayjs(editingAppAccess.validTo) : null}
                              onChange={(date: any) => setEditingAppAccess((prev) => prev ? { ...prev, validTo: date ? date.format("YYYY-MM-DD") : "" } : null)}
                            />
                          </Col>
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Custom Price</label>
                            <Input
                              style={{ width: "100%" }}
                              type="number"
                              placeholder="Override price"
                              value={editingAppAccess.customPrice}
                              onChange={(e) => setEditingAppAccess((prev) => prev ? { ...prev, customPrice: e.target.value } : null)}
                              prefix="₹"
                            />
                          </Col>
                          <Col lg="3" style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                            <Button color="primary" size="sm" disabled={editAccessSaving} onClick={handleSaveAppEdit}>
                              {editAccessSaving ? <LoadingSpinner size="sm" inline /> : "Save"}
                            </Button>
                            <Button color="secondary" size="sm" onClick={() => setEditingAppAccess(null)} disabled={editAccessSaving}>
                              Cancel
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />
        ) : (
          <Empty description="No applications assigned yet" />
        )}

        {/* Assigned Bundles */}
        <h6 style={{ fontWeight: 600, marginBottom: 12, marginTop: 24 }}>
          <GiftOutlined style={{ marginRight: 6, color: "#faad14" }} />
          Assigned Bundles
        </h6>
        {assignedBundlesLoading ? (
          <LoadingSpinner />
        ) : assignedBundles.length > 0 ? (
          <Collapse
            style={{ background: "transparent", border: "none" }}
            items={assignedBundles.map((ab: any) => {
              const bundleCost = ab.effectivePrice != null ? Number(ab.effectivePrice) : (ab.bundlePrice != null ? Number(ab.bundlePrice) : 0);
              const bundleApps: any[] = ab.apps || [];
              return {
                key: String(ab.customerBundleId),
                label: (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <GiftOutlined style={{ fontSize: 16, color: "#faad14" }} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{ab.bundleName || ab.bundle?.bundleName}</span>
                      <Tag color="warning" style={{ marginLeft: 2 }}>
                        {ab.pricingType || "Flat"}
                      </Tag>
                      {bundleCost > 0 && (
                        <Tag color="gold">
                          <span style={{ marginRight: 3 }}>₹</span>
                          {bundleCost.toFixed(2)}
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#8094ae" }}>
                      <span>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {ab.validFrom ? moment(ab.validFrom).format("DD MMM YYYY") : "-"}
                        {" — "}
                        {ab.validTo ? moment(ab.validTo).format("DD MMM YYYY") : "-"}
                      </span>
                      <span style={{ color: "#8094ae" }}>{bundleApps.length} app(s)</span>
                      <Tooltip title="Edit dates & price">
                        <EditOutlined
                          style={{ cursor: "pointer", color: "#6576ff", fontSize: 13 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingBundleAccess({
                              customerBundleId: ab.customerBundleId,
                              validFrom: ab.validFrom ? moment(ab.validFrom).format("YYYY-MM-DD") : "",
                              validTo: ab.validTo ? moment(ab.validTo).format("YYYY-MM-DD") : "",
                              effectivePrice: ab.effectivePrice ? String(Number(ab.effectivePrice)) : "",
                            });
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Revoke bundle">
                        {revokingBundleId === ab.customerBundleId ? (
                          <LoadingOutlined style={{ color: "#ff4d4f", fontSize: 13 }} />
                        ) : (
                          <DeleteOutlined
                            style={{ cursor: "pointer", color: "#ff4d4f", fontSize: 13 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (revokingBundleId) return;
                              handleRevokeBundle(ab);
                            }}
                          />
                        )}
                      </Tooltip>
                    </div>
                  </div>
                ),
                children: (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Inline Edit Form for Bundle */}
                    {editingBundleAccess && editingBundleAccess.customerBundleId === ab.customerBundleId && (
                      <div style={{ marginBottom: 8, padding: "12px 16px", background: "#fffbe6", borderRadius: 6, border: "1px solid #ffe58f" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Edit Date Range & Price</div>
                        <Row className="gy-2">
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Valid From</label>
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD MMM YYYY"
                              value={editingBundleAccess.validFrom ? dayjs(editingBundleAccess.validFrom) : null}
                              onChange={(date: any) => setEditingBundleAccess((prev) => prev ? { ...prev, validFrom: date ? date.format("YYYY-MM-DD") : "" } : null)}
                            />
                          </Col>
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Valid To</label>
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD MMM YYYY"
                              value={editingBundleAccess.validTo ? dayjs(editingBundleAccess.validTo) : null}
                              onChange={(date: any) => setEditingBundleAccess((prev) => prev ? { ...prev, validTo: date ? date.format("YYYY-MM-DD") : "" } : null)}
                            />
                          </Col>
                          <Col lg="3">
                            <label className="form-label" style={{ fontSize: 12 }}>Effective Price</label>
                            <Input
                              style={{ width: "100%" }}
                              type="number"
                              placeholder="Override price"
                              value={editingBundleAccess.effectivePrice}
                              onChange={(e) => setEditingBundleAccess((prev) => prev ? { ...prev, effectivePrice: e.target.value } : null)}
                              prefix="₹"
                            />
                          </Col>
                          <Col lg="3" style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                            <Button color="primary" size="sm" disabled={editAccessSaving} onClick={handleSaveBundleEdit}>
                              {editAccessSaving ? <LoadingSpinner size="sm" inline /> : "Save"}
                            </Button>
                            <Button color="secondary" size="sm" onClick={() => setEditingBundleAccess(null)} disabled={editAccessSaving}>
                              Cancel
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                    {bundleApps.length > 0 ? bundleApps.map((app: any) => (
                      <div key={app.appId}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <AppstoreOutlined style={{ color: "#6576ff", fontSize: 14 }} />
                            <span style={{ fontWeight: 500, fontSize: 13 }}>{app.appName || `App ${app.appId}`}</span>
                            {app.displayPrice != null && Number(app.displayPrice) > 0 && (
                              <Tag color="blue" style={{ fontSize: 11 }}>
                                ₹{Number(app.displayPrice).toFixed(2)}
                              </Tag>
                            )}
                          </div>
                          <Button
                            size="sm"
                            color={modifyingAppId === app.appId ? "secondary" : "primary"}
                            className="btn-dim"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => handleOpenModifyModules(app.appId)}
                          >
                            <EditOutlined style={{ marginRight: 4 }} />
                            {modifyingAppId === app.appId ? "Close" : "Modify Modules"}
                          </Button>
                        </div>
                        {modifyingAppId === app.appId && (
                          <div style={{ marginTop: 8, marginBottom: 4, padding: "12px 16px", background: "#f0f5ff", borderRadius: 6, border: "1px solid #d6e4ff" }}>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Toggle Module Access</div>
                            {modifyModulesLoading ? (
                              <LoadingSpinner size="sm" inline />
                            ) : modifyModulesList.length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
                                {modifyModulesList.map((mod) => (
                                  <Checkbox
                                    key={mod.moduleId}
                                    checked={mod.isAssigned}
                                    onChange={() => handleToggleModuleAccess(app.appId, mod.moduleId, mod.moduleName, mod.isAssigned)}
                                  >
                                    {mod.moduleName}
                                  </Checkbox>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: "#8094ae", fontSize: 13 }}>No modules found for this application.</span>
                            )}
                          </div>
                        )}
                      </div>
                    )) : (
                      <span style={{ color: "#8094ae", fontSize: 13 }}>No apps linked to this bundle</span>
                    )}
                  </div>
                ),
              };
            })}
          />
        ) : (
          <Empty description="No bundles assigned yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}

        {/* Activity History */}
        <h6 style={{ fontWeight: 600, marginTop: 32, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>
            <ClockCircleOutlined style={{ marginRight: 6, color: "#8094ae" }} />
            Activity History
          </span>
          <Tooltip title="Refresh history">
            <ReloadOutlined
              spin={logsLoading}
              style={{ cursor: "pointer", color: "#6576ff", fontSize: 14 }}
              onClick={() => fetchModuleAccessLogs()}
            />
          </Tooltip>
        </h6>
        {logsLoading ? (
          <LoadingSpinner />
        ) : moduleAccessLogs.length > 0 ? (
          <Timeline
            style={{ marginTop: 16, paddingLeft: 4 }}
            items={moduleAccessLogs.map((log: any, idx: number) => ({
              key: idx,
              color: log.action === "Assigned" || log.action === "Created" ? "green"
                : log.action === "Revoked" || log.action === "Deleted" ? "red"
                : log.action === "Updated" ? "blue"
                : "gray",
              children: (
                <div style={{ fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Tag
                      color={
                        log.action === "Assigned" || log.action === "Created" ? "success"
                          : log.action === "Revoked" || log.action === "Deleted" ? "error"
                          : log.action === "Updated" ? "processing"
                          : "default"
                      }
                      style={{ fontSize: 11 }}
                    >
                      {log.action}
                    </Tag>
                    <Tag style={{ fontSize: 11 }}>
                      {log.entityType === "SaCustomerModulePrices" ? "App Module" : log.entityType === "SaCustomerBundles" ? "Bundle" : log.entityType}
                    </Tag>
                    {log.remarks && <span style={{ color: "#364a63" }}>{log.remarks}</span>}
                  </div>
                  <div style={{ color: "#8094ae", fontSize: 11, marginTop: 4 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {moment(log.changedAt).format("DD MMM YYYY, hh:mm A")}
                  </div>
                  {(log.oldValue || log.newValue) && (
                    <div style={{ marginTop: 6, fontSize: 12, background: "#f8f9fa", padding: "6px 10px", borderRadius: 4 }}>
                      {log.oldValue && (
                        <div style={{ color: "#ff4d4f" }}>
                          <strong>Old:</strong>{" "}
                          {(() => {
                            try {
                              const parsed = JSON.parse(log.oldValue);
                              return Object.entries(parsed).map(([k, v]) => `${k}: ${v}`).join(", ");
                            } catch { return log.oldValue; }
                          })()}
                        </div>
                      )}
                      {log.newValue && (
                        <div style={{ color: "#52c41a" }}>
                          <strong>New:</strong>{" "}
                          {(() => {
                            try {
                              const parsed = JSON.parse(log.newValue);
                              return Object.entries(parsed).map(([k, v]) => `${k}: ${v}`).join(", ");
                            } catch { return log.newValue; }
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        ) : (
          <Empty description="No activity history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </>
    );
  };

  // --- Tab 3: User Limits ---
  const renderUserLimits = () => {
    if (userLimitsLoading)
      return <LoadingSpinner />;
    if (!userLimits)
      return <Empty description="No user limits configured" />;

    if (userLimitsEditMode) {
      return (
        <FormProvider {...userLimitsForm}>
          <form onSubmit={userLimitsForm.handleSubmit(saveUserLimits)}>
            <Row className="gy-3">
              <Col lg="3"><FormSelect name="userLimitType" label="Limit Type" options={userLimitTypeOptions} placeholder="-Select-" /></Col>
              <Col lg="3"><FormInput name="maxUsers" label="Max Users" type="number" placeholder="e.g. 10" /></Col>
              <Col lg="3"><FormInput name="maxProjects" label="Max Projects" type="number" placeholder="e.g. 5" /></Col>
              <Col lg="3"><FormInput name="maxSuppliers" label="Max Suppliers" type="number" placeholder="e.g. 5" /></Col>
              <Col lg="3"><FormInput name="maxStorage" label="Max Storage (bytes)" type="number" placeholder="e.g. 1073741824" /></Col>
            </Row>
            <div className="mt-4" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" type="submit" disabled={userLimitsSaving}>
                {userLimitsSaving ? <LoadingSpinner size="sm" inline /> : "Save Changes"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setUserLimitsEditMode(false)} disabled={userLimitsSaving}>
                Cancel
              </Button>
            </div>
          </form>
        </FormProvider>
      );
    }

    return (
      <>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Button color="primary" size="sm" className="btn-dim" onClick={startUserLimitsEdit}>
            <Icon name="edit" /> Edit Limits
          </Button>
        </div>
        <Row className="g-3">
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic
                title="Limit Type"
                value={userLimits.userLimitType || "Not Set"}
                valueStyle={{ fontSize: 20, color: userLimits.userLimitType === "Unlimited" ? "#52c41a" : "#1890ff" }}
              />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Max Users" value={userLimits.maxUsers} valueStyle={{ fontSize: 28, color: "#1890ff" }} prefix={<TeamOutlined />} />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Max Projects" value={userLimits.maxProjects} valueStyle={{ fontSize: 28, color: "#722ed1" }} prefix={<AppstoreOutlined />} />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Max Suppliers" value={userLimits.maxSuppliers} valueStyle={{ fontSize: 28, color: "#13c2c2" }} prefix={<ShopOutlined />} />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic
                title="Max Storage"
                value={userLimits.maxStorageFormatted || userLimits.maxStorage || "Not Set"}
                valueStyle={{ fontSize: 20, color: "#fa8c16" }}
              />
            </AntCard>
          </Col>
        </Row>
      </>
    );
  };

  // --- Tab 4: Coupons ---
  const renderCoupons = () => {
    return (
      <>
        {linkedCoupon ? (
          <AntCard
            size="small"
            style={{ maxWidth: 600 }}
            title={
              <span>
                <TagOutlined style={{ marginRight: 8 }} />
                Active Linked Coupon
              </span>
            }
            extra={
              <Button color="danger" size="sm" className="btn-dim" onClick={handleUnlinkCoupon}>
                Unlink Coupon
              </Button>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Coupon Code">
                <Tag color="blue" style={{ fontWeight: 600, fontSize: 14 }}>{linkedCoupon.couponCode}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Coupon Name">{linkedCoupon.couponName}</Descriptions.Item>
              <Descriptions.Item label="Discount">
                <Tag color="green">
                  {linkedCoupon.discountType === "Percent"
                    ? `${linkedCoupon.discountPercent || linkedCoupon.discountValue}%`
                    : `${Number(linkedCoupon.discountValue).toFixed(2)}`}
                </Tag>
                <span className="text-soft ms-1">({linkedCoupon.discountType})</span>
              </Descriptions.Item>
              <Descriptions.Item label="Validity">
                {moment(linkedCoupon.validFrom).format("DD MMM YYYY")} — {moment(linkedCoupon.validTo).format("DD MMM YYYY")}
              </Descriptions.Item>
            </Descriptions>
          </AntCard>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Empty
              description="No coupon linked to this customer"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Button
              color="primary"
              size="sm"
              className="mt-3"
              onClick={() => {
                loadCouponDropdown();
                setLinkCouponModal(true);
              }}
            >
              <PlusOutlined style={{ marginRight: 4 }} /> Link a Coupon
            </Button>
          </div>
        )}

        {linkedCoupon && (
          <p className="text-soft mt-3" style={{ fontSize: 12 }}>
            Each customer can have only one active linked coupon. To link a different coupon, unlink the current one first.
          </p>
        )}

        {/* Coupon History */}
        <div style={{ marginTop: 24 }}>
          <h6 className="title" style={{ marginBottom: 12 }}>Coupon History</h6>
          <Table
            dataSource={couponHistory}
            rowKey="customerCouponId"
            size="small"
            loading={couponHistoryLoading}
            pagination={false}
            locale={{ emptyText: <Empty description="No coupon history" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              {
                title: "Coupon Code",
                dataIndex: "couponCode",
                key: "couponCode",
                render: (val: string) => <Tag color="blue">{val}</Tag>,
              },
              {
                title: "Coupon Name",
                dataIndex: "couponName",
                key: "couponName",
              },
              {
                title: "Discount",
                key: "discount",
                render: (_: any, record: any) => (
                  <Tag color="green">
                    {record.discountType === "Percent"
                      ? `${record.discountPercent || record.discountValue}%`
                      : `${Number(record.discountValue).toFixed(2)}`}
                  </Tag>
                ),
              },
              {
                title: "Validity",
                key: "validity",
                render: (_: any, record: any) => (
                  <span>
                    {moment(record.validFrom).format("DD MMM YYYY")} — {moment(record.validTo).format("DD MMM YYYY")}
                  </span>
                ),
              },
              {
                title: "Assigned At",
                dataIndex: "assignedAt",
                key: "assignedAt",
                render: (val: string) => val ? moment(val).format("DD MMM YYYY") : "—",
              },
              {
                title: "Reference",
                dataIndex: "referenceFrom",
                key: "referenceFrom",
                render: (val: string) => val || "—",
              },
              {
                title: "Status",
                dataIndex: "isActive",
                key: "isActive",
                render: (val: boolean) =>
                  val ? <Tag color="green">Active</Tag> : <Tag color="red">Unlinked</Tag>,
              },
            ]}
          />
        </div>
      </>
    );
  };

  // --- Tab 7: Invoice Report ---
  const renderInvoices = () => {
    if (invoicesLoading)
      return <LoadingSpinner />;

    const invoiceStatusColorMap: Record<string, string> = {
      Draft: "default", Generated: "processing", Sent: "blue", Paid: "success",
      "Partially Paid": "warning", Cancelled: "error", Failed: "error",
    };
    const paymentStatusColorMap: Record<string, string> = {
      Unpaid: "orange", "Partially Paid": "gold", "Fully Paid": "green", Overdue: "red", Cancelled: "default",
    };
    const fmtInvCurrency = (amount: any) => {
      if (amount === null || amount === undefined) return "-";
      return `${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const openInvoiceView = async (record: any) => {
      setInvoiceViewModal(true);
      setInvoiceDetailLoading(true);
      try {
        const res = await superAdminService.getInvoiceById(record.invoiceId);
        if (res.status && res.data) {
          setSelectedInvoiceDetail(res.data);
        } else {
          Swal.fire("Error", res.message || "Invoice not found", "error");
          setInvoiceViewModal(false);
        }
      } catch (error: any) {
        Swal.fire("Error", error.message, "error");
        setInvoiceViewModal(false);
      } finally {
        setInvoiceDetailLoading(false);
      }
    };

    const numberToWordsINR = (num: number): string => {
      if (num === 0) return "Zero";
      const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
        "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
      const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
      const convert = (n: number): string => {
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
        if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
        if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
        if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
        return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
      };
      const rupees = Math.floor(num);
      const paise = Math.round((num - rupees) * 100);
      let result = "Indian Rupee " + convert(rupees);
      if (paise > 0) result += " and " + convert(paise) + " Paise";
      return result + " Only";
    };

    const loadLogoAsBase64 = (): Promise<string> => {
      return new Promise((resolve) => {
        try {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          };
          img.onerror = () => resolve("");
          img.src = require("../../../images/logo-dark2x.png");
        } catch { resolve(""); }
      });
    };

    const generatePdf = async (inv: any) => {
      const doc = new jsPDF();
      const fmtAmt = (v: any) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;

      const logoBase64 = await loadLogoAsBase64();

      // ========== PAGE BORDER ==========
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

      // ========== HEADER: Logo + Company Info (left) | TAX INVOICE (right) ==========
      let y = 14;
      const logoW = 38;
      const logoH = 14;
      if (logoBase64) {
        try { doc.addImage(logoBase64, "PNG", margin, y - 4, logoW, logoH); } catch {}
      }

      const companyX = margin + logoW + 6;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Procurex Technologies Private", companyX, y);
      doc.text("Limited", companyX, y + 5);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      const companyLines = [
        "H.No 4-49/2, Vattinagulapally",
        "Sy.No 237",
        "Gandipet, Hyderabad Telangana 500019",
        "India",
        "GSTIN 36AAOCP7271P1ZG",
        "8919901380",
        "bharath.devaram@procurextech.com",
        "www.procurextech.com",
      ];
      let companyY = y + 10;
      companyLines.forEach((line) => {
        doc.text(line, companyX, companyY);
        companyY += 3.5;
      });

      // TAX INVOICE on right
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("TAX INVOICE", pageWidth - margin, y + 6, { align: "right" });

      y = companyY + 4;

      // ========== INVOICE INFO SECTION ==========
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.rect(margin, y, contentWidth, 22);

      const midX = margin + contentWidth * 0.55;
      doc.line(midX, y, midX, y + 22);

      const infoLabelX = margin + 3;
      const infoValX = margin + 35;
      let iy = y + 5;
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);

      doc.setFont("helvetica", "normal");
      doc.text("#", infoLabelX, iy);
      doc.setFont("helvetica", "bold");
      doc.text(`: ${inv.invoiceNumber || "NA"}`, infoValX, iy);

      iy += 4.5;
      doc.setFont("helvetica", "normal");
      doc.text("Invoice Date", infoLabelX, iy);
      doc.setFont("helvetica", "bold");
      doc.text(`: ${inv.invoiceDate ? moment(inv.invoiceDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

      iy += 4.5;
      doc.setFont("helvetica", "normal");
      doc.text("Terms", infoLabelX, iy);
      doc.text(`: ${inv.billingPeriod || "Due on Receipt"}`, infoValX, iy);

      iy += 4.5;
      doc.setFont("helvetica", "normal");
      doc.text("Due Date", infoLabelX, iy);
      doc.setFont("helvetica", "bold");
      doc.text(`: ${inv.dueDate ? moment(inv.dueDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

      const rightLabelX = midX + 3;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Place Of Supply", rightLabelX, y + 5);
      doc.setFont("helvetica", "bold");
      doc.text(": Telangana (36)", rightLabelX + 30, y + 5);

      doc.setFont("helvetica", "normal");
      doc.text("Status", rightLabelX, y + 10);
      doc.setFont("helvetica", "bold");
      doc.text(`: ${inv.status || "Draft"}`, rightLabelX + 30, y + 10);

      doc.setFont("helvetica", "normal");
      doc.text("Payment", rightLabelX, y + 15);
      doc.setFont("helvetica", "bold");
      doc.text(`: ${inv.paymentStatus || "Unpaid"}`, rightLabelX + 30, y + 15);

      y += 22;

      // ========== BILL TO SECTION ==========
      doc.setFillColor(230, 230, 230);
      doc.rect(margin, y, contentWidth, 6, "F");
      doc.setDrawColor(0, 0, 0);
      doc.rect(margin, y, contentWidth, 6, "S");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Bill To", margin + 3, y + 4.2);

      y += 6;
      // Bill To content box - dynamic height based on available info
      const billToLines: string[] = [];
      if (inv.customerCode) billToLines.push(`Customer Code: ${inv.customerCode}`);
      if (inv.customerGstin) billToLines.push(`GSTIN: ${inv.customerGstin}`);
      if (inv.customerPan) billToLines.push(`PAN: ${inv.customerPan}`);
      const cityState = [inv.customerCity, inv.customerState].filter(Boolean).join(", ");
      if (cityState) billToLines.push(cityState + (inv.customerPincode ? ` - ${inv.customerPincode}` : ""));
      else if (inv.customerPincode) billToLines.push(`Pincode: ${inv.customerPincode}`);
      if (inv.customerPhone) billToLines.push(`Phone: ${inv.customerPhone}`);
      if (inv.customerEmail) billToLines.push(inv.customerEmail);
      billToLines.push(`Currency: ${inv.currencyCode || "INR"}`);

      const billToBoxH = 8 + billToLines.length * 4;
      doc.rect(margin, y, contentWidth, billToBoxH, "S");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(inv.customerName || "-", margin + 3, y + 5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      let billY = y + 9.5;
      billToLines.forEach((line) => {
        doc.text(line, margin + 3, billY);
        billY += 4;
      });

      y += billToBoxH;

      // ========== LINE ITEMS TABLE ==========
      const lines = inv.invoiceLines || [];
      const allLines = lines.map((l: any, i: number) => {
        const name = l.bundleId
          ? (l.bundleName || l.description || "-")
          : (l.appName || l.description || "-");
        const type = l.bundleId ? "[Enterprise]" : "[SaaS]";
        return [
          (i + 1).toString(),
          `${name} ${type}`,
          (l.quantity || 1).toString(),
          fmtAmt(l.unitPrice),
          fmtAmt(l.lineAmount || l.totalAmount),
        ];
      });

      y += 2;

      autoTable(doc, {
        startY: y,
        head: [["#", "Item & Description", "Qty", "Rate", "Amount"]],
        body: allLines,
        theme: "grid",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 7.5,
          cellPadding: 2.5,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        bodyStyles: {
          fontSize: 7.5,
          cellPadding: 2.5,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 80 },
          2: { cellWidth: 18, halign: "center" },
          3: { halign: "right" },
          4: { halign: "right", fontStyle: "bold" },
        },
        margin: { left: margin, right: margin },
      });

      y = (doc as any).lastAutoTable.finalY;

      // ========== SUMMARY SECTION ==========
      const summaryStartY = y + 2;
      const summaryRightW = 80;
      const summaryLeftW = contentWidth - summaryRightW;
      const summaryRightX = margin + summaryLeftW;

      const summaryRows: [string, string][] = [];
      summaryRows.push(["Sub Total", fmtAmt(inv.subtotalAmount)]);
      if (inv.couponAmount > 0) {
        const couponLabel = inv.couponCode ? `Coupon (${inv.couponCode.length > 18 ? inv.couponCode.substring(0, 18) + "..." : inv.couponCode})` : "Coupon Discount";
        summaryRows.push([couponLabel, "- " + fmtAmt(inv.couponAmount)]);
      }
      if (inv.manualDiscountAmount > 0) summaryRows.push(["Manual Discount", "- " + fmtAmt(inv.manualDiscountAmount)]);
      if (inv.totalDiscountAmount > 0) summaryRows.push(["Total Discount", "- " + fmtAmt(inv.totalDiscountAmount)]);
      if (inv.cgstAmount > 0) summaryRows.push([`CGST (${inv.cgstRate || 9}%)`, fmtAmt(inv.cgstAmount)]);
      if (inv.sgstAmount > 0) summaryRows.push([`SGST (${inv.sgstRate || 9}%)`, fmtAmt(inv.sgstAmount)]);
      if (inv.igstAmount > 0) summaryRows.push([`IGST (${inv.igstRate || 18}%)`, fmtAmt(inv.igstAmount)]);
      if (inv.taxAmount > 0 && !inv.cgstAmount && !inv.sgstAmount && !inv.igstAmount) summaryRows.push([`Tax (${inv.taxName || ""})`, fmtAmt(inv.taxAmount)]);
      if (inv.advanceAdjusted > 0) summaryRows.push(["Advance Adjusted", "- " + fmtAmt(inv.advanceAdjusted)]);

      let sy = summaryStartY;
      const labelColW = 52;
      const valColW = summaryRightW - labelColW;
      doc.setFontSize(7.5);

      summaryRows.forEach((row) => {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(summaryRightX, sy, labelColW, 5.5, "S");
        doc.rect(summaryRightX + labelColW, sy, valColW, 5.5, "S");
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(row[0], summaryRightX + labelColW - 2, sy + 4, { align: "right" });
        doc.text(row[1], summaryRightX + summaryRightW - 2, sy + 4, { align: "right" });
        sy += 5.5;
      });

      // Total row
      doc.setFillColor(240, 240, 240);
      doc.rect(summaryRightX, sy, labelColW, 6, "FD");
      doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("Total", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
      doc.text(`Rs.${fmtAmt(inv.totalAmount)}`, summaryRightX + summaryRightW - 2, sy + 4.5, { align: "right" });
      sy += 6;

      // Balance Due row
      doc.setFillColor(240, 240, 240);
      doc.rect(summaryRightX, sy, labelColW, 6, "FD");
      doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("Balance Due", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
      doc.text(`Rs.${fmtAmt(inv.balanceAmount || inv.netPayableAmount)}`, summaryRightX + summaryRightW - 2, sy + 4.5, { align: "right" });
      sy += 6;

      // Left side: Total in Words
      let ly = summaryStartY + 2;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text("Total In Words", margin, ly);
      ly += 4;
      doc.setFont("helvetica", "bolditalic");
      doc.setFontSize(7);
      const totalWords = numberToWordsINR(Number(inv.totalAmount || 0));
      const wordLines = doc.splitTextToSize(totalWords, summaryLeftW - 8);
      doc.text(wordLines, margin, ly);
      ly += wordLines.length * 3.5 + 4;

      // Notes
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.text("Notes", margin, ly);
      ly += 3.5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text(inv.notes || "Thanks for your business.", margin, ly);
      ly += 6;

      // Bank Details
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.text("Payment To Be Made to the Below Bank Account Details:", margin, ly);
      ly += 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      const bankLines = [
        "Account Name: Procurex Technologies Private Limited",
        "Bank Name: Indusind Bank",
        "Account Number: 255557895777",
        "IFSC Code: INDB0001801",
      ];
      bankLines.forEach((line) => {
        doc.text(line, margin, ly);
        ly += 3.5;
      });

      // Authorized Signature on right
      const authSigY = sy + 8;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(summaryRightX + 5, authSigY, summaryRightX + summaryRightW - 2, authSigY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text("Authorized Signature", summaryRightX + summaryRightW / 2, authSigY + 4, { align: "center" });

      // ========== TERMS & CONDITIONS ==========
      const termsY = Math.max(ly + 6, authSigY + 12);

      if (termsY + 60 > pageHeight - 15) {
        doc.addPage();
        ly = 14;
      } else {
        ly = termsY;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Terms & Conditions", margin, ly);
      ly += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      const terms = [
        '1.  The payment against this Tax Invoice shall be made in favour of "Procurex Technologies Private Limited" and shall be vide RTGS, NEFT, NACH, IMPS, Account Payee Cheque, DD or credit/debit card.',
        "2.  Delayed interest rate of 18% per annum will be applicable for any delays in payment beyond the terms of payment as mentioned above.",
        "3.  Any request for return and replacement of delivered products or any request for return and refund shall be handled only in accordance with the Return, Replacement and Refund process outlined in the above referenced Sales Order.",
        "4.  Input tax reversal on the credit note issued by Procurex Technologies Private Limited towards any discount including any rate difference shall be done by you in the same month when such credit notes are issued and shall be disclosed in your periodical GST return.",
        "5.  As per the provisions of the Income Tax Act, you shall deposit the deducted TDS with the government authorities within the prescribed time limit without fail. If any loss is incurred by Procurex Technologies Limited due to your non-compliance, you shall be liable to compensate for such loss.",
        "6.  In case of ex-works, Procurex Technologies Private Limited's responsibility ceases once the product(s) is/are available for delivery to you and Procurex Technologies Private Limited will not be responsible for any shortage, loss or damage in transit.",
      ];

      terms.forEach((term) => {
        const termLines = doc.splitTextToSize(term, contentWidth - 4);
        doc.text(termLines, margin, ly);
        ly += termLines.length * 3 + 2;
      });

      // Page border & page numbers on all pages
      const pageCount = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.setFont("helvetica", "normal");
        doc.text(String(i), pageWidth - margin, pageHeight - 12, { align: "right" });
      }

      doc.save(`Invoice-${inv.invoiceNumber || inv.invoiceId}.pdf`);
    };

    const handleDownloadPdf = async (record: any) => {
      try {
        const res = await superAdminService.getInvoiceById(record.invoiceId);
        if (res.status && res.data) generatePdf(res.data);
        else Swal.fire("Error", "Could not load invoice details", "error");
      } catch { Swal.fire("Error", "Failed to generate PDF", "error"); }
    };

    const invoiceColumns = [
      {
        title: "Invoice #",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        width: 130,
        render: (val: string, record: any) => (
          <a href="#view" className="text-primary fw-bold" onClick={(e) => { e.preventDefault(); openInvoiceView(record); }}>
            {val || "NA"}
          </a>
        ),
      },
      {
        title: "Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        width: 100,
        render: (date: string) => date ? moment(date).format("DD MMM YY") : "-",
      },
      {
        title: "Due",
        dataIndex: "dueDate",
        key: "dueDate",
        width: 100,
        render: (date: string, record: any) => {
          if (!date) return "-";
          const isPaid = parseFloat(record.balanceAmount || 0) <= 0;
          const isOverdue = !isPaid && moment(date).isBefore(moment(), "day");
          return <span style={{ color: isOverdue ? "#ff4d4f" : undefined, fontWeight: isOverdue ? 600 : undefined }}>
            {isOverdue && <ClockCircleOutlined style={{ marginRight: 2, fontSize: 10 }} />}{moment(date).format("DD MMM YY")}
          </span>;
        },
      },
      {
        title: "Period",
        dataIndex: "billingPeriod",
        key: "period",
        width: 100,
        render: (text: string, record: any) => {
          if (text) return text;
          if (record.billingMonth) return moment(record.billingMonth).format("MMM YY");
          return "-";
        },
      },
      {
        title: "Type",
        dataIndex: "sourceType",
        key: "sourceType",
        width: 70,
        render: (type: string) => type ? <Tag color={type === "Auto" ? "cyan" : "purple"} style={{ fontSize: 11 }}>{type}</Tag> : "-",
      },
      {
        title: "Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 100,
        render: (val: number) => <span style={{ fontWeight: 600 }}>{fmtInvCurrency(val)}</span>,
      },
      {
        title: "Advance",
        dataIndex: "advanceAdjusted",
        key: "advanceAdjusted",
        width: 90,
        render: (val: number) => {
          const v = Number(val || 0);
          return v > 0 ? <span style={{ color: "#722ed1", fontWeight: 500 }}>{fmtInvCurrency(v)}</span> : <span style={{ color: "#bbb" }}>-</span>;
        },
      },
      {
        title: "Paid",
        dataIndex: "paidAmount",
        key: "paidAmount",
        width: 100,
        render: (val: number) => <span style={{ color: "#52c41a" }}>{fmtInvCurrency(val || 0)}</span>,
      },
      {
        title: "Balance",
        dataIndex: "balanceAmount",
        key: "balanceAmount",
        width: 100,
        render: (val: number) => {
          const b = Number(val || 0);
          return <span style={{ fontWeight: 600, color: b > 0 ? "#fa8c16" : "#52c41a" }}>{fmtInvCurrency(Math.max(b, 0))}</span>;
        },
      },
      {
        title: "Payment",
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        width: 100,
        render: (status: string) => <Tag color={paymentStatusColorMap[status] || "default"}>{status || "-"}</Tag>,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 80,
        render: (status: string) => <Tag color={invoiceStatusColorMap[status] || "default"}>{status}</Tag>,
      },
      {
        title: "",
        key: "actions",
        width: 120,
        render: (_: any, record: any) => (
          <div style={{ display: "flex", gap: 6 }}>
            <Tooltip title="View"><EyeOutlined style={{ cursor: "pointer", color: "#1890ff", fontSize: 14 }} onClick={() => openInvoiceView(record)} /></Tooltip>
            <Tooltip title="Download PDF"><FilePdfOutlined style={{ cursor: "pointer", color: "#595959", fontSize: 14 }} onClick={() => handleDownloadPdf(record)} /></Tooltip>
            {(record.status === "Draft" || record.status === "Generated") && (
              <Tooltip title="Send"><MailOutlined style={{ cursor: "pointer", color: "#1890ff", fontSize: 14 }} onClick={async () => {
                try {
                  const res = await superAdminService.sendInvoice({ invoiceId: record.invoiceId });
                  if (res.status) { Swal.fire("Success", "Invoice sent", "success"); fetchInvoices(); }
                  else Swal.fire("Error", res.message, "error");
                } catch (e: any) { Swal.fire("Error", e?.response?.data?.message || e.message, "error"); }
              }} /></Tooltip>
            )}
            {(record.status === "Draft" || record.status === "Generated") && (
              <Tooltip title="Cancel"><StopOutlined style={{ cursor: "pointer", color: "#ff4d4f", fontSize: 14 }} onClick={async () => {
                const result = await Swal.fire({ title: "Cancel Invoice?", icon: "warning", showCancelButton: true, confirmButtonText: "Yes", confirmButtonColor: "#d33", input: "text", inputLabel: "Reason (optional)", inputPlaceholder: "Cancellation reason" });
                if (!result.isConfirmed) return;
                try {
                  const res = await superAdminService.cancelInvoice({ invoiceId: record.invoiceId, reason: result.value || undefined });
                  if (res.status) { Swal.fire("Cancelled", "Invoice cancelled", "success"); fetchInvoices(); }
                  else Swal.fire("Error", res.message, "error");
                } catch (e: any) { Swal.fire("Error", e?.response?.data?.message || e.message, "error"); }
              }} /></Tooltip>
            )}
            {Number(record.balanceAmount) > 0 && billingConfig?.advancePaymentAllowed && (
              <Tooltip title="Adjust from Advance"><WalletOutlined style={{ cursor: "pointer", color: "#722ed1", fontSize: 14 }} onClick={async () => {
                const result = await Swal.fire({ title: "Adjust from Advance", text: `Adjust advance towards invoice ${record.invoiceNumber}?`, icon: "question", showCancelButton: true, confirmButtonText: "Adjust", confirmButtonColor: "#722ed1", input: "number", inputLabel: "Amount (empty = auto)", inputPlaceholder: "Auto-fill if empty", inputAttributes: { min: "0.01", step: "0.01" } });
                if (!result.isConfirmed) return;
                try {
                  const payload: any = { invoiceId: record.invoiceId };
                  if (result.value && Number(result.value) > 0) payload.amount = Number(result.value);
                  const res = await superAdminService.adjustFromAdvance(payload);
                  if (res.status) { Swal.fire("Success", res.message || "Advance adjusted", "success"); fetchInvoices(); fetchPayments(); }
                  else Swal.fire("Error", res.message, "error");
                } catch (e: any) { Swal.fire("Error", e?.response?.data?.message || e.message, "error"); }
              }} /></Tooltip>
            )}
          </div>
        ),
      },
    ];

    return (
      <>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          {!showCreateInvoice && (
            <Button color="primary" size="sm" onClick={openCreateInvoiceModal}>
              <Icon name="plus" /> Create Invoice
            </Button>
          )}
        </div>

        {/* Inline Create Invoice Section */}
        {showCreateInvoice && (
          <AntCard
            size="small"
            title={<span style={{ fontWeight: 600, fontSize: 15 }}><FileTextOutlined style={{ marginRight: 8 }} />Create Invoice for {customer.partyName}</span>}
            extra={<Button color="secondary" size="sm" onClick={closeCreateInvoiceSection} disabled={createInvoiceLoading}>Close</Button>}
            style={{ marginBottom: 20, border: "1px solid #d9d9d9" }}
          >
            {billingConfig && billingConfig.billingMode !== "Manual" && (
              <div style={{ background: "#fff2e8", border: "1px solid #ffbb96", borderRadius: 6, padding: "10px 16px", marginBottom: 16, fontSize: 13 }}>
                <strong>Note:</strong> Manual invoice creation is only allowed for customers with <strong>Manual</strong> billing mode. Current mode: <Tag color="orange">{billingConfig.billingMode}</Tag>
              </div>
            )}

            <Row className="g-3">
              {/* Left: Customer Info + Coupon + Tax + Form Fields */}
              <Col lg="7">
                {/* Billing Info */}
                <AntCard size="small" style={{ marginBottom: 12, background: "#f9f9f9" }}>
                  <Row className="g-2">
                    <Col sm="4">
                      <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase" }}>Billing Mode</div>
                      <div style={{ fontWeight: 600 }}>{billingConfig?.billingMode || "-"}</div>
                    </Col>
                    <Col sm="4">
                      <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase" }}>Currency</div>
                      <div style={{ fontWeight: 600 }}>{customer.currencyCode || "-"}</div>
                    </Col>
                    <Col sm="4">
                      <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase" }}>Billing Frequency</div>
                      <div style={{ fontWeight: 600 }}>{billingConfig?.billingFrequency || "-"}</div>
                    </Col>
                  </Row>
                </AntCard>

                {/* Coupon */}
                {linkedCoupon && (
                  <AntCard size="small" style={{ marginBottom: 12, background: "#f6ffed", borderColor: "#b7eb8f" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <TagOutlined style={{ color: "#52c41a" }} />
                      <span style={{ fontWeight: 600 }}>Active Coupon:</span>
                      <Tag color="green">{linkedCoupon.couponCode}</Tag>
                      <span style={{ fontSize: 13 }}>
                        {linkedCoupon.discountType === "Percent"
                          ? `${linkedCoupon.discountPercent}% off`
                          : `Flat ${Number(linkedCoupon.discountValue).toFixed(2)} off`}
                      </span>
                      <span style={{ fontSize: 12, color: "#8094ae", marginLeft: "auto" }}>
                        {moment(linkedCoupon.validFrom).format("DD MMM YY")} — {moment(linkedCoupon.validTo).format("DD MMM YY")}
                      </span>
                    </div>
                  </AntCard>
                )}

                {/* Tax */}
                {taxConfig && taxConfig.taxName && (
                  <AntCard size="small" style={{ marginBottom: 12, background: "#fff7e6", borderColor: "#ffd591" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <CalculatorOutlined style={{ color: "#fa8c16" }} />
                      <span style={{ fontWeight: 600 }}>Tax:</span>
                      <Tag color="orange">{taxConfig.taxName} ({taxConfig.taxRate}%)</Tag>
                    </div>
                  </AntCard>
                )}

                {/* Billing Period */}
                <div className="form-group mb-3">
                  <label className="form-label">Billing Period</label>
                  <Input
                    placeholder="e.g., Jan 2025, Q1 2025"
                    value={invoiceBillingPeriod}
                    onChange={(e) => setInvoiceBillingPeriod(e.target.value)}
                    maxLength={50}
                  />
                </div>

                {/* Manual Discount */}
                <div className="form-group mb-3">
                  <label className="form-label">Manual Discount (optional)</label>
                  <Row className="g-2">
                    <Col sm="4">
                      <Select
                        placeholder="Discount Type"
                        style={{ width: "100%" }}
                        allowClear
                        value={invoiceDiscountType}
                        onChange={(val) => {
                          setInvoiceDiscountType(val);
                          if (!val) setInvoiceDiscountValue(undefined);
                        }}
                        options={[
                          { value: "Flat", label: "Flat Amount" },
                          { value: "Percentage", label: "Percentage (%)" },
                        ]}
                      />
                    </Col>
                    <Col sm="4">
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={invoiceDiscountType === "Percentage" ? "e.g., 10" : "e.g., 500"}
                        min={0}
                        max={invoiceDiscountType === "Percentage" ? 20 : undefined}
                        disabled={!invoiceDiscountType}
                        value={invoiceDiscountValue}
                        onChange={(val) => setInvoiceDiscountValue(val || undefined)}
                      />
                    </Col>
                    <Col sm="4">
                      <Input
                        placeholder="Discount reason"
                        disabled={!invoiceDiscountType}
                        value={invoiceDiscountReason}
                        onChange={(e) => setInvoiceDiscountReason(e.target.value)}
                      />
                    </Col>
                  </Row>
                  {invoiceDiscountType === "Percentage" && (
                    <div style={{ fontSize: 12, color: "#8094ae", marginTop: 4 }}>Max 20% manual discount allowed</div>
                  )}
                </div>

                {/* Notes */}
                <div className="form-group mb-3">
                  <label className="form-label">Notes (optional)</label>
                  <Input.TextArea
                    rows={2}
                    maxLength={500}
                    value={invoiceNotes}
                    onChange={(e) => setInvoiceNotes(e.target.value)}
                    placeholder="Any additional notes for this invoice..."
                  />
                </div>

                {/* Advance Payment Adjustment */}
                {billingConfig?.advancePaymentAllowed && invoiceAdvanceBalance > 0 && (
                  <AntCard
                    size="small"
                    style={{ marginBottom: 12, borderColor: "#722ed1" }}
                    title={<span><WalletOutlined style={{ marginRight: 6, color: "#722ed1" }} />Advance Payment Adjustment</span>}
                  >
                    <div style={{ marginBottom: 12, fontSize: 13 }}>
                      Available advance balance: <strong style={{ color: "#722ed1" }}>
                        {invoiceAdvanceBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                    <Row className="g-2">
                      <Col sm="5">
                        <Checkbox
                          checked={invoiceUseAdvance}
                          onChange={(e) => {
                            setInvoiceUseAdvance(e.target.checked);
                            if (e.target.checked) {
                              // Auto-fill: compute net before advance from current selections
                              const activeMods = moduleAccess.filter((m: any) => m.isActive && !m.customerBundleId);
                              const aMap = new Map<number, any>();
                              for (const m of activeMods) { if (m.appId && !aMap.has(m.appId)) aMap.set(m.appId, { appPrice: Number(m.finalPrice) || Number(m.appPrice) || 0 }); }
                              const appSub = selectedInvoiceAppIds.reduce((s, id) => s + (aMap.get(id)?.appPrice || 0), 0);
                              const bndSub = customerBundles.filter((b: any) => selectedInvoiceBundleIds.includes(b.bundleId)).reduce((s: number, b: any) => s + (Number(b.effectivePrice) || Number(b.bundlePrice) || 0), 0);
                              const sub = appSub + bndSub;
                              let disc = 0;
                              if (invoiceDiscountType && invoiceDiscountValue && invoiceDiscountValue > 0) {
                                disc = invoiceDiscountType === "Percentage" ? sub * (invoiceDiscountValue / 100) : invoiceDiscountValue;
                              } else if (linkedCoupon && sub > 0) {
                                disc = linkedCoupon.discountType === "Percent" ? sub * ((linkedCoupon.discountPercent || 0) / 100) : Math.min(Number(linkedCoupon.discountValue) || 0, sub);
                              }
                              const nba = Math.max(sub - disc, 0);
                              const autoAmt = Math.min(invoiceAdvanceBalance, nba);
                              setInvoiceAdvanceToAdjust(autoAmt > 0 ? Math.round(autoAmt * 100) / 100 : undefined);
                            } else {
                              setInvoiceAdvanceToAdjust(undefined);
                            }
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>Deduct from Advance</span>
                        </Checkbox>
                      </Col>
                      {invoiceUseAdvance && (
                        <Col sm="5">
                          <InputNumber
                            style={{ width: "100%" }}
                            value={invoiceAdvanceToAdjust}
                            onChange={(val) => setInvoiceAdvanceToAdjust(val ?? undefined)}
                            min={0.01}
                            max={invoiceAdvanceBalance}
                            precision={2}
                            prefix="₹"
                            placeholder="Amount to deduct"
                          />
                          <div style={{ fontSize: 11, color: "#8094ae", marginTop: 2 }}>
                            Max: {invoiceAdvanceBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </AntCard>
                )}
              </Col>

              {/* Right: App + Bundle Selection with Prices + Breakup + Actions */}
              <Col lg="5">
                {/* App Selection */}
                <div className="form-group mb-3">
                  <label className="form-label" style={{ fontWeight: 600 }}>
                    <AppstoreOutlined style={{ marginRight: 4 }} />Select SaaS Pricing to Invoice <span className="mandatory">*</span>
                  </label>
                  {(() => {
                    const activeModules = moduleAccess.filter((m: any) => m.isActive && !m.customerBundleId);
                    // Deduplicate by appId (exclude bundle-assigned apps)
                    const appMap = new Map<number, any>();
                    for (const m of activeModules) {
                      if (m.appId && !appMap.has(m.appId)) {
                        appMap.set(m.appId, {
                          appId: m.appId,
                          appName: m.appName || `Application ${m.appId}`,
                          appPrice: Number(m.finalPrice) || Number(m.appPrice) || 0,
                        });
                      }
                    }
                    const apps = Array.from(appMap.values());
                    if (apps.length === 0) return <Empty description="No active applications found" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

                    return (
                      <>
                        <div style={{ marginBottom: 8 }}>
                          <Checkbox
                            checked={selectedInvoiceAppIds.length === apps.length && apps.length > 0}
                            indeterminate={selectedInvoiceAppIds.length > 0 && selectedInvoiceAppIds.length < apps.length}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedInvoiceAppIds(apps.map((a: any) => a.appId));
                              else setSelectedInvoiceAppIds([]);
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>Select All ({apps.length} apps)</span>
                          </Checkbox>
                        </div>
                        <div style={{ maxHeight: 200, overflowY: "auto", padding: "8px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                          {apps.map((app: any) => (
                            <div key={app.appId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
                              <Checkbox
                                checked={selectedInvoiceAppIds.includes(app.appId)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedInvoiceAppIds((prev) => [...prev, app.appId]);
                                  else setSelectedInvoiceAppIds((prev) => prev.filter((id) => id !== app.appId));
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>{app.appName}</span>
                              </Checkbox>
                              <span style={{ fontSize: 13, color: "#595959", fontWeight: 600 }}>
                                {app.appPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Bundle Selection */}
                {customerBundles.length > 0 && (
                  <div className="form-group mb-3">
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      <GiftOutlined style={{ marginRight: 4 }} />Select Enterprise Pricing to Invoice
                    </label>
                    <div style={{ maxHeight: 150, overflowY: "auto", padding: "8px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                      {customerBundles.map((bundle: any) => {
                        const price = Number(bundle.effectivePrice) || Number(bundle.bundlePrice) || 0;
                        return (
                          <div key={bundle.bundleId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
                            <Checkbox
                              checked={selectedInvoiceBundleIds.includes(bundle.bundleId)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedInvoiceBundleIds((prev) => [...prev, bundle.bundleId]);
                                else setSelectedInvoiceBundleIds((prev) => prev.filter((id) => id !== bundle.bundleId));
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>{bundle.bundleName}</span>
                              {bundle.bundleCode && <Tag style={{ marginLeft: 6, fontSize: 11 }}>{bundle.bundleCode}</Tag>}
                            </Checkbox>
                            <span style={{ fontSize: 13, color: "#595959", fontWeight: 600 }}>
                              {price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Live Price Breakup */}
                {(() => {
                  const activeModules = moduleAccess.filter((m: any) => m.isActive);
                  const appMap = new Map<number, any>();
                  for (const m of activeModules) {
                    if (m.appId && !appMap.has(m.appId)) {
                      appMap.set(m.appId, { appId: m.appId, appName: m.appName || `App ${m.appId}`, appPrice: Number(m.finalPrice) || Number(m.appPrice) || 0 });
                    }
                  }
                  const selectedApps = Array.from(appMap.values()).filter((a: any) => selectedInvoiceAppIds.includes(a.appId));
                  const selectedBundles = customerBundles.filter((b: any) => selectedInvoiceBundleIds.includes(b.bundleId));

                  const appSubtotal = selectedApps.reduce((sum: number, a: any) => sum + (a.appPrice || 0), 0);
                  const bundleSubtotal = selectedBundles.reduce((sum: number, b: any) => sum + (Number(b.effectivePrice) || Number(b.bundlePrice) || 0), 0);
                  const subtotal = appSubtotal + bundleSubtotal;

                  let couponDiscount = 0;
                  if (linkedCoupon && subtotal > 0) {
                    if (linkedCoupon.discountType === "Percent") {
                      couponDiscount = subtotal * ((linkedCoupon.discountPercent || 0) / 100);
                    } else {
                      couponDiscount = Math.min(Number(linkedCoupon.discountValue) || 0, subtotal);
                    }
                  }

                  let manualDiscount = 0;
                  if (invoiceDiscountType && invoiceDiscountValue && invoiceDiscountValue > 0) {
                    if (invoiceDiscountType === "Percentage") {
                      manualDiscount = subtotal * (invoiceDiscountValue / 100);
                    } else {
                      manualDiscount = invoiceDiscountValue;
                    }
                  }

                  const totalDiscount = manualDiscount > 0 ? manualDiscount : couponDiscount;
                  const netBeforeAdvance = Math.max(subtotal - totalDiscount, 0);
                  const advanceDeduction = invoiceUseAdvance && invoiceAdvanceToAdjust ? Math.min(invoiceAdvanceToAdjust, netBeforeAdvance) : 0;
                  const taxableAmount = Math.max(netBeforeAdvance - advanceDeduction, 0);
                  const taxRate = taxConfig?.taxRate ? Number(taxConfig.taxRate) : 0;
                  const taxAmount = taxableAmount * (taxRate / 100);
                  const totalAmount = taxableAmount + taxAmount;
                  const fmt = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                  return (
                    <AntCard size="small" style={{ marginBottom: 12, background: "#f0f5ff", borderColor: "#adc6ff" }}>
                      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Invoice Breakup</div>
                      <table style={{ width: "100%", fontSize: 13 }}>
                        <tbody>
                          {selectedApps.map((app: any) => (
                            <tr key={app.appId}>
                              <td style={{ color: "#595959" }}><AppstoreOutlined style={{ marginRight: 4, color: "#6576ff" }} />{app.appName}</td>
                              <td style={{ textAlign: "right", fontWeight: 500 }}>{fmt(app.appPrice)}</td>
                            </tr>
                          ))}
                          {selectedBundles.map((b: any) => (
                            <tr key={b.bundleId}>
                              <td style={{ color: "#595959" }}><GiftOutlined style={{ marginRight: 4, color: "#722ed1" }} />{b.bundleName}</td>
                              <td style={{ textAlign: "right", fontWeight: 500 }}>{fmt(Number(b.effectivePrice) || Number(b.bundlePrice) || 0)}</td>
                            </tr>
                          ))}
                          <tr><td colSpan={2}><hr style={{ margin: "6px 0", borderColor: "#d9d9d9" }} /></td></tr>
                          <tr style={{ fontWeight: 600 }}>
                            <td>Subtotal</td>
                            <td style={{ textAlign: "right" }}>{fmt(subtotal)}</td>
                          </tr>
                          {couponDiscount > 0 && manualDiscount === 0 && (
                            <tr style={{ color: "#52c41a" }}>
                              <td>Coupon ({linkedCoupon?.couponCode})</td>
                              <td style={{ textAlign: "right" }}>- {fmt(couponDiscount)}</td>
                            </tr>
                          )}
                          {manualDiscount > 0 && (
                            <tr style={{ color: "#fa8c16" }}>
                              <td>Manual Discount {invoiceDiscountType === "Percentage" ? `(${invoiceDiscountValue}%)` : "(Flat)"}</td>
                              <td style={{ textAlign: "right" }}>- {fmt(manualDiscount)}</td>
                            </tr>
                          )}
                          {manualDiscount > 0 && couponDiscount > 0 && (
                            <tr><td colSpan={2} style={{ fontSize: 11, color: "#8094ae" }}>Manual discount overrides coupon</td></tr>
                          )}
                          {advanceDeduction > 0 && (
                            <>
                              <tr>
                                <td>Net Before Advance</td>
                                <td style={{ textAlign: "right" }}>{fmt(netBeforeAdvance)}</td>
                              </tr>
                              <tr style={{ color: "#722ed1" }}>
                                <td><WalletOutlined style={{ marginRight: 4 }} />Advance Adjusted</td>
                                <td style={{ textAlign: "right" }}>- {fmt(advanceDeduction)}</td>
                              </tr>
                            </>
                          )}
                          <tr style={{ fontWeight: 600 }}>
                            <td>Taxable Amount</td>
                            <td style={{ textAlign: "right" }}>{fmt(taxableAmount)}</td>
                          </tr>
                          {taxRate > 0 && (
                            <tr>
                              <td>Tax ({taxConfig?.taxName} @ {taxRate}%)</td>
                              <td style={{ textAlign: "right" }}>+ {fmt(taxAmount)}</td>
                            </tr>
                          )}
                          <tr><td colSpan={2}><hr style={{ margin: "6px 0", borderColor: "#1890ff" }} /></td></tr>
                          <tr style={{ fontWeight: 700, fontSize: 15 }}>
                            <td>Total</td>
                            <td style={{ textAlign: "right", color: "#1890ff" }}>{customer.currencyCode || ""} {fmt(totalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </AntCard>
                  );
                })()}

                {/* Actions */}
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleCreateInvoice}
                    disabled={createInvoiceLoading || (selectedInvoiceAppIds.length === 0 && selectedInvoiceBundleIds.length === 0)}
                  >
                    {createInvoiceLoading ? <LoadingSpinner size="sm" inline /> : "Create Invoice"}
                  </Button>
                  <Button color="secondary" size="sm" onClick={closeCreateInvoiceSection} disabled={createInvoiceLoading}>
                    Cancel
                  </Button>
                </div>
              </Col>
            </Row>
          </AntCard>
        )}

        {invoiceSummary && (
          <Row className="g-3 mb-3">
            <Col sm="6" lg="3">
              <AntCard size="small" hoverable className="cp-stat-card">
                <Statistic title="Total Invoices" value={invoiceSummary.total} valueStyle={{ fontSize: 28, color: "#1890ff" }} />
              </AntCard>
            </Col>
            <Col sm="6" lg="3">
              <AntCard size="small" hoverable className="cp-stat-card">
                <Statistic title="Total Amount" value={invoiceSummary.totalAmount?.toFixed(2)} valueStyle={{ fontSize: 20, color: "#722ed1" }} prefix="₹" />
              </AntCard>
            </Col>
            <Col sm="6" lg="3">
              <AntCard size="small" hoverable className="cp-stat-card">
                <Statistic title="Paid" value={invoiceSummary.paidAmount?.toFixed(2)} valueStyle={{ fontSize: 20, color: "#52c41a" }} suffix={<span style={{ fontSize: 12 }}>({invoiceSummary.paidCount})</span>} />
              </AntCard>
            </Col>
            <Col sm="6" lg="3">
              <AntCard size="small" hoverable className="cp-stat-card">
                <Statistic title="Pending" value={invoiceSummary.pendingAmount?.toFixed(2)} valueStyle={{ fontSize: 20, color: "#fa8c16" }} suffix={<span style={{ fontSize: 12 }}>({invoiceSummary.pendingCount})</span>} />
              </AntCard>
            </Col>
          </Row>
        )}

        {invoices.length > 0 ? (
          <Table
            columns={invoiceColumns}
            dataSource={invoices}
            rowKey={(r) => r.invoiceId || r.id || Math.random()}
            size="small"
            bordered
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }}
            scroll={{ x: 800 }}
            className="cp-module-table"
          />
        ) : (
          <Empty description="No invoices found for this customer" />
        )}

        {/* Invoice View Modal */}
        <Modal isOpen={invoiceViewModal} toggle={() => setInvoiceViewModal(false)} size="xl" style={{ maxWidth: "1000px" }}>
          <ModalBody style={{ padding: "20px 24px" }}>
            {invoiceDetailLoading ? (
              <LoadingSpinner height="300px" />
            ) : selectedInvoiceDetail ? (() => {
              const inv = selectedInvoiceDetail;
              const cc = inv.currencyCode || customer?.currencyCode || "INR";
              const fmtD = (v: any) => `${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const lines = inv.invoiceLines || [];
              const appLines = lines.filter((l: any) => l.appId && !l.bundleId);
              const bundleLines = lines.filter((l: any) => l.bundleId);

              return (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 className="title mb-1" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <EyeOutlined /> Invoice: {inv.invoiceNumber || "NA"}
                        <Tag color={invoiceStatusColorMap[inv.status] || "default"}>{inv.status}</Tag>
                        <Tag color={paymentStatusColorMap[inv.paymentStatus] || "default"}>{inv.paymentStatus}</Tag>
                      </h5>
                      <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                        {inv.sourceType && <Tag color={inv.sourceType === "Auto" ? "cyan" : "purple"} style={{ fontSize: 11 }}>{inv.sourceType}</Tag>}
                        {cc}
                        {inv.sentAt && <span style={{ marginLeft: 8 }}><MailOutlined style={{ marginRight: 3 }} />Sent: {moment(inv.sentAt).format("DD MMM YYYY, hh:mm A")}</span>}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button color="secondary" size="sm" className="btn-dim" onClick={() => generatePdf(inv)}>
                        <FilePdfOutlined style={{ marginRight: 4 }} /> Download
                      </Button>
                      {(inv.status === "Draft" || inv.status === "Generated") && (
                        <Button color="primary" size="sm" className="btn-dim" onClick={async () => {
                          try {
                            const res = await superAdminService.sendInvoice({ invoiceId: inv.invoiceId });
                            if (res.status) {
                              Swal.fire("Success", "Invoice sent successfully", "success");
                              setInvoiceViewModal(false);
                              fetchInvoices();
                            } else {
                              Swal.fire("Error", res.message || "Failed to send", "error");
                            }
                          } catch (error: any) {
                            Swal.fire("Error", error?.response?.data?.message || error.message, "error");
                          }
                        }}>
                          <MailOutlined style={{ marginRight: 4 }} /> Send
                        </Button>
                      )}
                      {inv.status === "Sent" && (
                        <Button color="info" size="sm" className="btn-dim" onClick={async () => {
                          try {
                            const res = await superAdminService.resendInvoice({ invoiceId: inv.invoiceId });
                            if (res.status) {
                              Swal.fire("Success", "Invoice resent successfully", "success");
                              setInvoiceViewModal(false);
                              fetchInvoices();
                            } else {
                              Swal.fire("Error", res.message || "Failed to resend", "error");
                            }
                          } catch (error: any) {
                            Swal.fire("Error", error?.response?.data?.message || error.message, "error");
                          }
                        }}>
                          <MailOutlined style={{ marginRight: 4 }} /> Resend
                        </Button>
                      )}
                      {(inv.status === "Draft" || inv.status === "Generated") && (
                        <Button color="danger" size="sm" className="btn-dim" onClick={async () => {
                          const result = await Swal.fire({
                            title: "Cancel Invoice?",
                            text: "Are you sure you want to cancel this invoice?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Yes, cancel it",
                            confirmButtonColor: "#d33",
                            input: "text",
                            inputLabel: "Reason (optional)",
                            inputPlaceholder: "Enter cancellation reason",
                          });
                          if (!result.isConfirmed) return;
                          try {
                            const res = await superAdminService.cancelInvoice({ invoiceId: inv.invoiceId, reason: result.value || undefined });
                            if (res.status) {
                              Swal.fire("Cancelled", "Invoice cancelled successfully", "success");
                              setInvoiceViewModal(false);
                              fetchInvoices();
                            } else {
                              Swal.fire("Error", res.message || "Failed to cancel", "error");
                            }
                          } catch (error: any) {
                            Swal.fire("Error", error?.response?.data?.message || error.message, "error");
                          }
                        }}>
                          <StopOutlined style={{ marginRight: 4 }} /> Cancel
                        </Button>
                      )}
                      {inv.balanceAmount > 0 && billingConfig?.advancePaymentAllowed && (
                        <Button color="primary" size="sm" className="btn-dim" style={{ borderColor: "#722ed1", color: "#722ed1", background: "#f9f0ff" }} onClick={async () => {
                          const result = await Swal.fire({
                            title: "Adjust from Advance",
                            html: `Adjust advance payment towards invoice <b>${inv.invoiceNumber}</b>?<br/>Outstanding: <b>${Number(inv.balanceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b>`,
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonText: "Adjust",
                            confirmButtonColor: "#722ed1",
                            input: "number",
                            inputLabel: "Amount (leave empty for auto)",
                            inputPlaceholder: "Auto-fill if empty",
                            inputAttributes: { min: "0.01", step: "0.01" },
                          });
                          if (!result.isConfirmed) return;
                          try {
                            const payload: any = { invoiceId: inv.invoiceId };
                            if (result.value && Number(result.value) > 0) payload.amount = Number(result.value);
                            const res = await superAdminService.adjustFromAdvance(payload);
                            if (res.status) {
                              Swal.fire("Success", res.message || "Advance adjusted successfully", "success");
                              setInvoiceViewModal(false);
                              fetchInvoices();
                              fetchPayments();
                            } else {
                              Swal.fire("Error", res.message || "Failed to adjust", "error");
                            }
                          } catch (error: any) {
                            Swal.fire("Error", error?.response?.data?.message || error.message, "error");
                          }
                        }}>
                          <WalletOutlined style={{ marginRight: 4 }} /> Adjust from Advance
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col">
                      <AntCard size="small" style={{ borderLeft: "3px solid #1890ff" }}>
                        <Statistic title="Amount" value={fmtD(inv.totalAmount)} prefix={cc} valueStyle={{ color: "#1890ff", fontSize: 16 }} />
                      </AntCard>
                    </div>
                    {Number(inv.advanceAdjusted || 0) > 0 && (
                      <div className="col">
                        <AntCard size="small" style={{ borderLeft: "3px solid #722ed1" }}>
                          <Statistic title="Advance" value={fmtD(inv.advanceAdjusted)} prefix={cc} valueStyle={{ color: "#722ed1", fontSize: 16 }} />
                        </AntCard>
                      </div>
                    )}
                    <div className="col">
                      <AntCard size="small" style={{ borderLeft: "3px solid #52c41a" }}>
                        <Statistic title="Paid" value={fmtD(inv.paidAmount)} prefix={cc} valueStyle={{ color: "#52c41a", fontSize: 16 }} />
                      </AntCard>
                    </div>
                    <div className="col">
                      <AntCard size="small" style={{ borderLeft: "3px solid #fa8c16" }}>
                        <Statistic title="Balance" value={fmtD(Math.max(Number(inv.balanceAmount || 0), 0))} prefix={cc} valueStyle={{ color: "#fa8c16", fontSize: 16 }} />
                      </AntCard>
                    </div>
                    <div className="col">
                      <AntCard size="small" style={{ borderLeft: "3px solid #13c2c2" }}>
                        <Statistic title="Net Payable" value={fmtD(inv.netPayableAmount)} prefix={cc} valueStyle={{ color: "#13c2c2", fontSize: 16 }} />
                      </AntCard>
                    </div>
                  </div>

                  <AntCard title="Invoice Information" size="small" className="mb-3">
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
                      <Descriptions.Item label="Invoice #">{inv.invoiceNumber || "NA"}</Descriptions.Item>
                      <Descriptions.Item label="Invoice Date">{inv.invoiceDate ? moment(inv.invoiceDate).format("DD MMM YYYY") : "-"}</Descriptions.Item>
                      <Descriptions.Item label="Due Date">
                        {inv.dueDate ? (() => {
                          const isPaid = parseFloat(inv.balanceAmount || 0) <= 0;
                          const isOverdue = !isPaid && moment(inv.dueDate).isBefore(moment(), "day");
                          return (
                            <span style={{ color: isOverdue ? "#ff4d4f" : undefined }}>
                              {moment(inv.dueDate).format("DD MMM YYYY")}
                              {isOverdue && " (Overdue)"}
                            </span>
                          );
                        })() : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Billing Period">{inv.billingPeriod || "-"}</Descriptions.Item>
                      <Descriptions.Item label="Currency"><Tag color="blue">{cc}</Tag></Descriptions.Item>
                      {inv.emailStatus && inv.emailStatus !== "Not Sent" && (
                        <Descriptions.Item label="Email">
                          <Tag color={inv.emailStatus === "Sent" ? "green" : "red"}>{inv.emailStatus}</Tag>
                          {inv.sentTo && <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>{inv.sentTo}</span>}
                        </Descriptions.Item>
                      )}
                      {inv.status === "Failed" && (
                        <Descriptions.Item label="Failure" span={2}>
                          <Tag color="red" icon={<ExclamationCircleOutlined />}>{inv.failureReason || "Unknown"}</Tag>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </AntCard>

                  {appLines.length > 0 && (
                    <AntCard title={<span><AppstoreOutlined style={{ marginRight: 6, color: "#1890ff" }} />SaaS Pricing ({appLines.length})</span>} size="small" className="mb-3">
                      <Table dataSource={appLines} rowKey="invoiceLineId" pagination={false} size="small" bordered
                        columns={[
                          { title: "#", key: "idx", width: 40, render: (_: any, __: any, i: number) => i + 1 },
                          { title: "Application", dataIndex: "appName", key: "appName", render: (v: string, r: any) => v || r.description || "-" },
                          { title: "Qty", dataIndex: "quantity", key: "quantity", width: 50, render: (v: any) => v || 1 },
                          { title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice", width: 100, render: (v: any) => fmtD(v) },
                          { title: "Amount", dataIndex: "lineAmount", key: "lineAmount", width: 100, render: (v: any, r: any) => <strong>{fmtD(v || r.totalAmount)}</strong> },
                        ]}
                        summary={() => {
                          const total = appLines.reduce((s: number, l: any) => s + Number(l.lineAmount || l.totalAmount || 0), 0);
                          return <Table.Summary.Row><Table.Summary.Cell index={0} colSpan={4} align="right"><strong>Subtotal</strong></Table.Summary.Cell><Table.Summary.Cell index={4}><strong style={{ color: "#1890ff" }}>{fmtD(total)}</strong></Table.Summary.Cell></Table.Summary.Row>;
                        }}
                      />
                    </AntCard>
                  )}

                  {bundleLines.length > 0 && (
                    <AntCard title={<span><GiftOutlined style={{ marginRight: 6, color: "#722ed1" }} />Enterprise Pricing ({bundleLines.length})</span>} size="small" className="mb-3">
                      <Table dataSource={bundleLines} rowKey="invoiceLineId" pagination={false} size="small" bordered
                        columns={[
                          { title: "#", key: "idx", width: 40, render: (_: any, __: any, i: number) => i + 1 },
                          { title: "Bundle", dataIndex: "bundleName", key: "bundleName", render: (v: string, r: any) => v || r.description || "-" },
                          { title: "Qty", dataIndex: "quantity", key: "quantity", width: 50, render: (v: any) => v || 1 },
                          { title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice", width: 100, render: (v: any) => fmtD(v) },
                          { title: "Amount", dataIndex: "lineAmount", key: "lineAmount", width: 100, render: (v: any, r: any) => <strong>{fmtD(v || r.totalAmount)}</strong> },
                        ]}
                        summary={() => {
                          const total = bundleLines.reduce((s: number, l: any) => s + Number(l.lineAmount || l.totalAmount || 0), 0);
                          return <Table.Summary.Row><Table.Summary.Cell index={0} colSpan={4} align="right"><strong>Subtotal</strong></Table.Summary.Cell><Table.Summary.Cell index={4}><strong style={{ color: "#722ed1" }}>{fmtD(total)}</strong></Table.Summary.Cell></Table.Summary.Row>;
                        }}
                      />
                    </AntCard>
                  )}

                  <AntCard title="Invoice Breakup" size="small" className="mb-3">
                    <div style={{ maxWidth: 420, marginLeft: "auto" }}>
                      <Descriptions column={1} bordered size="small" labelStyle={{ width: 180 }}>
                        <Descriptions.Item label="Subtotal">{fmtD(inv.subtotalAmount)}</Descriptions.Item>
                        {inv.couponAmount > 0 && <Descriptions.Item label={<span>Coupon {inv.couponCode && <Tag color="green" style={{ fontSize: 10 }}>{inv.couponCode}</Tag>}</span>}><span className="text-danger">-{fmtD(inv.couponAmount)}</span></Descriptions.Item>}
                        {inv.manualDiscountAmount > 0 && <Descriptions.Item label={`Manual Discount (${inv.manualDiscountType || ""})`}><span style={{ color: "#fa8c16" }}>-{fmtD(inv.manualDiscountAmount)}</span></Descriptions.Item>}
                        <Descriptions.Item label={<strong>Taxable Amount</strong>}><strong>{fmtD(inv.taxableAmount)}</strong></Descriptions.Item>
                        {inv.taxAmount > 0 && <Descriptions.Item label={`Tax (${inv.taxName || ""})`}>{fmtD(inv.taxAmount)}</Descriptions.Item>}
                        {inv.advanceAdjusted > 0 && <Descriptions.Item label="Advance Adjusted"><span className="text-danger">-{fmtD(inv.advanceAdjusted)}</span></Descriptions.Item>}
                        <Descriptions.Item label={<strong style={{ fontSize: 13 }}>Total Amount</strong>}><strong style={{ fontSize: 15, color: "#1890ff" }}>{cc} {fmtD(inv.totalAmount)}</strong></Descriptions.Item>
                        {inv.paidAmount > 0 && <Descriptions.Item label="Paid"><span style={{ color: "#52c41a", fontWeight: 600 }}>{fmtD(inv.paidAmount)}</span></Descriptions.Item>}
                        {inv.balanceAmount > 0 && <Descriptions.Item label={<strong>Balance Due</strong>}><strong style={{ color: "#fa8c16" }}>{fmtD(inv.balanceAmount)}</strong></Descriptions.Item>}
                      </Descriptions>
                    </div>
                  </AntCard>

                  {inv.advanceAdjustments && inv.advanceAdjustments.length > 0 && (
                    <AntCard title="Advance Adjustments" size="small" className="mb-3">
                      <Table dataSource={inv.advanceAdjustments} rowKey="adjustmentId" pagination={false} size="small" bordered
                        columns={[
                          { title: "Ref", dataIndex: "advanceReferenceNo", key: "ref", render: (v: string) => v || "-" },
                          { title: "Amount", dataIndex: "adjustedAmount", key: "amt", render: (v: any) => fmtD(v) },
                          { title: "Date", dataIndex: "adjustmentDate", key: "date", render: (v: string) => v ? moment(v).format("DD MMM YYYY") : "-" },
                        ]}
                      />
                    </AntCard>
                  )}

                  {inv.notes && (
                    <AntCard size="small" className="mb-3" style={{ background: "#fffbe6", borderColor: "#ffe58f" }}>
                      <strong>Notes:</strong> {inv.notes}
                      {inv.discountReason && <><Divider type="vertical" /><strong>Discount Reason:</strong> {inv.discountReason}</>}
                    </AntCard>
                  )}

                  <div className="mt-3 text-end">
                    <Button color="secondary" size="sm" onClick={() => setInvoiceViewModal(false)}>Close</Button>
                  </div>
                </>
              );
            })() : null}
          </ModalBody>
        </Modal>
      </>
    );
  };

  // --- Tab 8: Payment Report ---
  const renderPayments = () => {
    if (paymentsLoading)
      return <LoadingSpinner />;

    if (!receivableDetail) {
      return <Empty description="No payment data found for this customer" />;
    }

    const summary = receivableDetail.summary || receivableDetail;
    const payments = receivableDetail.payments || receivableDetail.paymentHistory || [];
    const advances = receivableDetail.advances || [];

    const paymentColumns = [
      {
        title: "Ref #",
        dataIndex: "paymentNumber",
        key: "paymentNumber",
        width: 160,
        fixed: "left" as const,
        ellipsis: true,
        render: (val: string) => <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{val || "-"}</span>,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 90,
        ellipsis: true,
        render: (type: string) => (
          <Tag color={type === "Advance" ? "purple" : "blue"} style={{ fontSize: 11 }}>
            {type === "Advance" ? "Advance" : "Payment"}
          </Tag>
        ),
      },
      {
        title: "Date",
        dataIndex: "paymentDate",
        key: "paymentDate",
        width: 110,
        ellipsis: true,
        render: (date: string) => <span style={{ whiteSpace: "nowrap" }}>{date ? moment(date).format("DD MMM YYYY") : "-"}</span>,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 120,
        ellipsis: true,
        render: (val: number, record: any) => {
          const formatted = val != null ? `${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-";
          return <span style={{ color: record.type === "Advance" ? "#722ed1" : undefined, fontWeight: 600, whiteSpace: "nowrap" }}>{formatted}</span>;
        },
      },
      {
        title: "Mode",
        dataIndex: "paymentMode",
        key: "paymentMode",
        width: 120,
        ellipsis: true,
        render: (val: string) => val || "-",
      },
      {
        title: "Reference",
        dataIndex: "referenceNumber",
        key: "referenceNumber",
        width: 180,
        ellipsis: true,
        render: (val: string) => <Tooltip title={val}><span style={{ whiteSpace: "nowrap" }}>{val || "-"}</span></Tooltip>,
      },
      {
        title: "Invoice #",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        width: 170,
        ellipsis: true,
        render: (val: string, record: any) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {val || "-"}
            {record.invoiceAmount != null && (
              <div style={{ fontSize: 11, color: "#8094ae" }}>
                Amt: {Number(record.invoiceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        ellipsis: true,
        render: (status: string) => {
          const colorMap: Record<string, string> = {
            Completed: "green", Cleared: "green", Pending: "orange", Failed: "red", Refunded: "default", Cancelled: "default",
          };
          return <Tag color={colorMap[status] || "blue"}>{status || "Recorded"}</Tag>;
        },
      },
      {
        title: "Action",
        key: "action",
        width: 180,
        fixed: "right" as const,
        render: (_: any, record: any) => {
          if (record.status === "Pending") {
            return (
              <div style={{ display: "flex", gap: 6 }}>
                <Tooltip title="Approve Payment">
                  <span
                    style={{ color: "#52c41a", cursor: "pointer", fontSize: 16 }}
                    onClick={async () => {
                      const result = await Swal.fire({
                        title: "Approve Payment?",
                        html: `<b>${record.paymentNumber}</b> — ₹${Number(record.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}<br/>This will update the invoice as paid.`,
                        icon: "question",
                        showCancelButton: true,
                        confirmButtonText: "Approve",
                        confirmButtonColor: "#52c41a",
                      });
                      if (!result.isConfirmed) return;
                      try {
                        const res = await superAdminService.approvePayment({ paymentId: record.paymentId || record.id });
                        if (res.status) {
                          Swal.fire("Approved!", res.message || "Payment approved successfully.", "success");
                          fetchPayments();
                          fetchInvoices();
                        } else {
                          Swal.fire("Error", res.message || "Failed to approve", "error");
                        }
                      } catch (e: any) {
                        Swal.fire("Error", e?.response?.data?.message || e.message, "error");
                      }
                    }}
                  >
                    <CheckCircleOutlined />
                  </span>
                </Tooltip>
                <Tooltip title="Reject Payment">
                  <span
                    style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
                    onClick={async () => {
                      const result = await Swal.fire({
                        title: "Reject Payment?",
                        html: `<b>${record.paymentNumber}</b> — ₹${Number(record.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                        icon: "warning",
                        input: "text",
                        inputPlaceholder: "Reason for rejection (optional)",
                        showCancelButton: true,
                        confirmButtonText: "Reject",
                        confirmButtonColor: "#ff4d4f",
                      });
                      if (!result.isConfirmed) return;
                      try {
                        const res = await superAdminService.rejectPayment({
                          paymentId: record.paymentId || record.id,
                          reason: result.value || undefined,
                        });
                        if (res.status) {
                          Swal.fire("Rejected!", res.message || "Payment rejected.", "success");
                          fetchPayments();
                        } else {
                          Swal.fire("Error", res.message || "Failed to reject", "error");
                        }
                      } catch (e: any) {
                        Swal.fire("Error", e?.response?.data?.message || e.message, "error");
                      }
                    }}
                  >
                    <StopOutlined />
                  </span>
                </Tooltip>
              </div>
            );
          }
          if (record.remarks) {
            return <span style={{ fontSize: 12, color: "#8094ae" }}>{record.remarks}</span>;
          }
          return null;
        },
      },
    ];

    return (
      <>
        {/* Summary */}
        <Row className="g-3 mb-3">
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Total Billed" value={Number(summary.totalBilled || summary.totalAmount || 0).toFixed(2)} valueStyle={{ fontSize: 20, color: "#1890ff" }} prefix="₹" />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Total Paid" value={Number(summary.totalPaid || summary.paidAmount || 0).toFixed(2)} valueStyle={{ fontSize: 20, color: "#52c41a" }} prefix="₹" />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Outstanding" value={Math.max(Number(summary.outstanding || summary.pendingAmount || 0), 0).toFixed(2)} valueStyle={{ fontSize: 20, color: "#fa8c16" }} prefix="₹" />
            </AntCard>
          </Col>
          <Col sm="6" lg="3">
            <AntCard size="small" hoverable className="cp-stat-card">
              <Statistic title="Advance Balance" value={Number(summary.advanceBalance || 0).toFixed(2)} valueStyle={{ fontSize: 20, color: "#722ed1" }} prefix="₹" />
            </AntCard>
          </Col>
        </Row>

        {/* Payment History */}
        <div className="mb-2" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h6 style={{ fontWeight: 600, margin: 0 }}>Payment History</h6>
          <Button color="primary" size="sm" onClick={() => { setShowRecordPayment(!showRecordPayment); if (showRecordPayment) { setRpInvoiceId(undefined); setRpDate(null); setRpAmount(undefined); setRpMethod(undefined); setRpReference(""); setRpBankName(""); setRpRemarks(""); } }}>
            <WalletOutlined style={{ marginRight: 4 }} /> {showRecordPayment ? "Cancel" : "Record Payment"}
          </Button>
        </div>

        {showRecordPayment && (
          <AntCard size="small" className="mb-3" style={{ borderColor: "#6576ff" }}>
            <h6 className="mb-3" style={{ fontWeight: 600 }}>
              <WalletOutlined style={{ marginRight: 8, color: "#6576ff" }} />
              Record Payment
            </h6>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Invoice <span className="text-danger">*</span></label>
                <Select
                  style={{ width: "100%" }}
                  value={rpInvoiceId}
                  onChange={(val) => {
                    setRpInvoiceId(val);
                    const inv = (receivableDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === val);
                    if (inv && inv.balanceAmount) {
                      setRpAmount(Number(Number(inv.balanceAmount).toFixed(2)));
                    }
                  }}
                  placeholder="Select invoice"
                  showSearch
                  optionFilterProp="label"
                  options={(receivableDetail?.invoices || [])
                    .filter((inv: any) => Number(inv.balanceAmount) > 0)
                    .map((inv: any) => ({
                      value: inv.invoiceId || inv.id,
                      label: `${inv.invoiceNumber || "INV-" + (inv.invoiceId || inv.id)} — Bal: ₹${Number(inv.balanceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                    }))}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                <DatePicker style={{ width: "100%" }} value={rpDate} onChange={(val) => setRpDate(val)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Amount <span className="text-danger">*</span></label>
                <InputNumber
                  style={{ width: "100%" }}
                  value={rpAmount}
                  onChange={(val) => setRpAmount(val ?? undefined)}
                  min={0.01}
                  max={(() => {
                    const inv = (receivableDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === rpInvoiceId);
                    return inv ? Number(Number(inv.balanceAmount).toFixed(2)) : undefined;
                  })()}
                  precision={2}
                  prefix="₹"
                  status={(() => {
                    if (!rpInvoiceId || !rpAmount) return undefined;
                    const inv = (receivableDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === rpInvoiceId);
                    if (inv && rpAmount > Number(Number(inv.balanceAmount).toFixed(2))) return "error" as const;
                    return undefined;
                  })()}
                />
                {rpInvoiceId && rpAmount && (() => {
                  const inv = (receivableDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === rpInvoiceId);
                  if (inv && rpAmount > Number(Number(inv.balanceAmount).toFixed(2))) {
                    return <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>Amount cannot exceed invoice balance (₹{Number(inv.balanceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })})</div>;
                  }
                  return null;
                })()}
              </div>
              <div className="col-md-4">
                <label className="form-label">Method <span className="text-danger">*</span></label>
                <Select style={{ width: "100%" }} value={rpMethod} onChange={(val) => setRpMethod(val)} options={paymentMethodOptions} placeholder="Select method" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Reference No <span className="text-danger">*</span></label>
                <Input value={rpReference} onChange={(e) => setRpReference(e.target.value)} maxLength={100} placeholder="Transaction reference" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Bank Name</label>
                <Input value={rpBankName} onChange={(e) => setRpBankName(e.target.value)} maxLength={100} placeholder="Optional" />
              </div>
              <div className="col-md-8">
                <label className="form-label">Remarks</label>
                <Input value={rpRemarks} onChange={(e) => setRpRemarks(e.target.value)} maxLength={500} placeholder="Optional" />
              </div>
            </div>
            <div className="mt-3" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" onClick={handleRecordPayment}
                disabled={rpSaving || !rpInvoiceId || !rpDate || !rpAmount || !rpMethod || !rpReference || (() => {
                  const inv = (receivableDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === rpInvoiceId);
                  return inv ? rpAmount > Number(Number(inv.balanceAmount).toFixed(2)) : false;
                })()}>
                {rpSaving ? <LoadingSpinner size="sm" inline /> : "Record Payment"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setShowRecordPayment(false)} disabled={rpSaving}>Cancel</Button>
            </div>
          </AntCard>
        )}
        {payments.length > 0 ? (
          <Table
            columns={paymentColumns}
            dataSource={[...payments].sort((a, b) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""))}
            rowKey={(r) => r.paymentId || r.id || Math.random()}
            size="small"
            bordered
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }}
            scroll={{ x: 1400 }}
            className="cp-module-table"
          />
        ) : (
          <Empty description="No payment records found" style={{ padding: "20px 0" }} />
        )}

        {/* Advance Payments */}
        <div className="mt-4 mb-2" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h6 style={{ fontWeight: 600, margin: 0 }}>Advance Payments</h6>
          {billingConfig?.advancePaymentAllowed && (
            <Button
              color="primary"
              size="sm"
              className="btn-dim"
              onClick={() => setShowAdvanceForm(!showAdvanceForm)}
            >
              <PlusOutlined style={{ marginRight: 4 }} /> Record Advance Payment
            </Button>
          )}
        </div>

        {showAdvanceForm && (
          <AntCard size="small" className="mb-3" style={{ maxWidth: 700 }}>
            <Row className="gy-2">
              <Col lg="4">
                <label className="form-label">Amount <span className="text-danger">*</span></label>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.01}
                  step={0.01}
                  placeholder="Amount"
                  value={advFormAmount}
                  onChange={(val) => setAdvFormAmount(val)}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                <DatePicker
                  style={{ width: "100%" }}
                  value={advFormDate ? dayjs(advFormDate) : null}
                  onChange={(_, dateStr) => setAdvFormDate(dateStr as string)}
                  format="YYYY-MM-DD"
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Payment Method</label>
                <Select
                  style={{ width: "100%" }}
                  value={advFormMethod}
                  onChange={(val) => setAdvFormMethod(val)}
                  placeholder="Select"
                  options={[
                    { value: "BankTransfer", label: "Bank Transfer" },
                    { value: "NEFT", label: "NEFT" },
                    { value: "RTGS", label: "RTGS" },
                    { value: "IMPS", label: "IMPS" },
                    { value: "UPI", label: "UPI" },
                    { value: "Cheque", label: "Cheque" },
                    { value: "Cash", label: "Cash" },
                    { value: "Card", label: "Card" },
                    { value: "Other", label: "Other" },
                  ]}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Reference Number</label>
                <Input
                  value={advFormRefNo}
                  onChange={(e) => setAdvFormRefNo(e.target.value)}
                  placeholder="Transaction Ref #"
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Bank Name</label>
                <Input
                  value={advFormBank}
                  onChange={(e) => setAdvFormBank(e.target.value.replace(/[^a-zA-Z ]/g, ""))}
                  placeholder="Bank name"
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Remarks</label>
                <Input
                  value={advFormRemarks}
                  onChange={(e) => setAdvFormRemarks(e.target.value)}
                  placeholder="Remarks"
                />
              </Col>
            </Row>
            <div className="mt-3" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" onClick={handleRecordAdvance} disabled={advFormSaving}>
                {advFormSaving ? <LoadingSpinner size="sm" inline /> : "Submit"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => { setShowAdvanceForm(false); resetAdvanceForm(); }}>
                Cancel
              </Button>
            </div>
          </AntCard>
        )}

        {/* Inline Edit Form */}
        {editingAdvanceId && (
          <AntCard size="small" className="mb-3" style={{ maxWidth: 700, borderColor: "#6576ff" }}>
            <h6 className="mb-2" style={{ fontWeight: 600, color: "#6576ff" }}>Edit Advance Payment</h6>
            <Row className="gy-2">
              <Col lg="4">
                <label className="form-label">Amount</label>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.01}
                  step={0.01}
                  value={editAdvData.amount}
                  onChange={(val) => setEditAdvData({ ...editAdvData, amount: val })}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Payment Date</label>
                <DatePicker
                  style={{ width: "100%" }}
                  value={editAdvData.paymentDate ? dayjs(editAdvData.paymentDate) : null}
                  onChange={(_, dateStr) => setEditAdvData({ ...editAdvData, paymentDate: dateStr as string })}
                  format="YYYY-MM-DD"
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Payment Method</label>
                <Select
                  style={{ width: "100%" }}
                  value={editAdvData.method}
                  onChange={(val) => setEditAdvData({ ...editAdvData, method: val })}
                  options={[
                    { value: "BankTransfer", label: "Bank Transfer" },
                    { value: "NEFT", label: "NEFT" },
                    { value: "RTGS", label: "RTGS" },
                    { value: "IMPS", label: "IMPS" },
                    { value: "UPI", label: "UPI" },
                    { value: "Cheque", label: "Cheque" },
                    { value: "Cash", label: "Cash" },
                    { value: "Card", label: "Card" },
                    { value: "Other", label: "Other" },
                  ]}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Reference Number</label>
                <Input
                  value={editAdvData.referenceNumber}
                  onChange={(e) => setEditAdvData({ ...editAdvData, referenceNumber: e.target.value })}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Bank Name</label>
                <Input
                  value={editAdvData.bankName}
                  onChange={(e) => setEditAdvData({ ...editAdvData, bankName: e.target.value.replace(/[^a-zA-Z ]/g, "") })}
                />
              </Col>
              <Col lg="4">
                <label className="form-label">Remarks</label>
                <Input
                  value={editAdvData.remarks}
                  onChange={(e) => setEditAdvData({ ...editAdvData, remarks: e.target.value })}
                />
              </Col>
            </Row>
            <div className="mt-3" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" onClick={handleUpdateAdvance} disabled={advFormSaving}>
                {advFormSaving ? <LoadingSpinner size="sm" inline /> : "Update"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => { setEditingAdvanceId(null); setEditAdvData({}); }}>
                Cancel
              </Button>
            </div>
          </AntCard>
        )}

        {advances.length > 0 ? (
          <Table
            columns={[
              { title: "Ref No", dataIndex: "advanceReferenceNo", key: "advanceReferenceNo", width: 140, render: (v: string) => <span style={{ fontWeight: 600 }}>{v || "-"}</span> },
              { title: "Advance Amount", dataIndex: "advanceAmount", key: "advanceAmount", width: 130, render: (v: number) => v != null ? Number(v).toFixed(2) : "-" },
              { title: "Advance Date", dataIndex: "advanceDate", key: "advanceDate", width: 120, render: (d: string) => d ? moment(d).format("DD MMM YYYY") : "-" },
              { title: "Method", dataIndex: "paymentMethod", key: "paymentMethod", width: 110, render: (v: string) => v || "-" },
              { title: "Reference", dataIndex: "referenceNumber", key: "referenceNumber", width: 130, render: (v: string) => v || "-" },
              { title: "Unadjusted Balance", dataIndex: "unadjustedBalance", key: "unadjustedBalance", width: 150, render: (v: number) => v != null ? Number(v).toFixed(2) : "-" },
              {
                title: "Status", dataIndex: "status", key: "status", width: 100,
                render: (status: string) => {
                  const colorMap: Record<string, string> = { Available: "green", "Partially Used": "orange", "Fully Used": "default", Cancelled: "red" };
                  return <Tag color={colorMap[status] || "blue"}>{status || "-"}</Tag>;
                },
              },
              {
                title: "Actions", key: "actions", width: 100, fixed: "right" as const,
                render: (_: any, record: any) => {
                  const canEdit = record.status === "Available" || record.status === "Partially Used";
                  const canCancel = record.status === "Available";
                  if (!canEdit && !canCancel) return <span style={{ color: "#999" }}>-</span>;
                  return (
                    <div style={{ display: "flex", gap: 8 }}>
                      {canEdit && (
                        <Tooltip title="Edit">
                          <EditOutlined
                            style={{ color: "#6576ff", cursor: "pointer", fontSize: 15 }}
                            onClick={() => startEditAdvance(record)}
                          />
                        </Tooltip>
                      )}
                      {canCancel && (
                        <Tooltip title="Cancel">
                          <StopOutlined
                            style={{ color: "#f5222d", cursor: "pointer", fontSize: 15 }}
                            onClick={() => handleCancelAdvance(record)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  );
                },
              },
            ]}
            dataSource={advances}
            rowKey={(r) => r.advancePaymentId || r.id || Math.random()}
            size="small"
            bordered
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }}
            scroll={{ x: 950 }}
            className="cp-module-table"
          />
        ) : (
          <Empty description="No advance payments recorded" style={{ padding: "20px 0" }} />
        )}
      </>
    );
  };

  // --- Tab 5: Tax ---
  const renderTax = () => {
    if (taxLoading)
      return <LoadingSpinner />;
    if (!taxConfig)
      return <Empty description="No tax configuration found" />;

    const taxTypeColor: Record<string, string> = {
      INDIRECT: "blue",
      TDS: "orange",
      TCS: "purple",
      CESS: "cyan",
    };

    if (taxEditMode) {
      return (
        <AntCard
          size="small"
          title={
            <span>
              <CalculatorOutlined style={{ marginRight: 8 }} />
              Change Tax Configuration
            </span>
          }
          style={{ maxWidth: 500 }}
        >
          <div className="form-group mb-3">
            <label className="form-label">Select Tax</label>
            <Select
              placeholder="Search and select a tax..."
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              value={selectedTaxId}
              onChange={(val) => setSelectedTaxId(val)}
              options={taxOptions}
              size="large"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button color="primary" size="sm" onClick={saveTaxConfig} disabled={taxSaving || !selectedTaxId}>
              {taxSaving ? <LoadingSpinner size="sm" inline /> : "Save Changes"}
            </Button>
            <Button color="secondary" size="sm" onClick={() => { setTaxEditMode(false); setSelectedTaxId(taxConfig.taxId || undefined); }} disabled={taxSaving}>
              Cancel
            </Button>
          </div>
        </AntCard>
      );
    }

    if (!taxConfig.taxId) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Empty
            description="No tax configured for this customer"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
          <Button
            color="primary"
            size="sm"
            className="mt-3"
            onClick={() => setTaxEditMode(true)}
          >
            <PlusOutlined style={{ marginRight: 4 }} /> Configure Tax
          </Button>
        </div>
      );
    }

    return (
      <>
        <div style={{ background: "#f8f9fc", border: "1px solid #e9ecef", borderRadius: 8, padding: "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px 32px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>Tax Name</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#364a63" }}>{taxConfig.taxName || "—"}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>Tax Rate</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#1890ff" }}>{Number(taxConfig.taxRate).toFixed(2)}%</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>Tax Type</span>
              <Tag color={taxTypeColor[taxConfig.taxType || ""] || "default"} style={{ fontSize: 12, width: "fit-content" }}>
                {taxConfig.taxType || "—"}
              </Tag>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button color="primary" size="sm" className="btn-dim" onClick={() => setTaxEditMode(true)}>
            <EditOutlined style={{ marginRight: 4 }} /> Change Tax
          </Button>
        </div>
      </>
    );
  };

  // --- Tab 6: Access Control ---
  const renderAccessControl = () => {
    if (accessLoading)
      return <LoadingSpinner />;
    if (!accessStatus)
      return <Empty description="Access control information not available" />;

    const isOnHold = accessStatus.billingStatus === "Hold";
    const isDraft = accessStatus.billingStatus === "Draft";
    const statusColor = isOnHold ? "#ff4d4f" : isDraft ? "#faad14" : "#52c41a";
    const statusBg = isOnHold ? "#fff2f0" : isDraft ? "#fffbe6" : "#f6ffed";

    return (
      <Row className="g-4">
        {/* Left: Status & Actions */}
        <Col lg="7">
          {/* Status Banner */}
          <AntCard
            size="small"
            className="mb-3"
            style={{ border: `1px solid ${statusColor}30`, background: statusBg }}
            bodyStyle={{ padding: "16px 20px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Billing Status</div>
                  <Tag color={isOnHold ? "error" : isDraft ? "warning" : "success"} style={{ fontSize: 14, padding: "2px 12px", fontWeight: 600 }}>
                    {accessStatus.billingStatus}
                  </Tag>
                </div>
                <Divider type="vertical" style={{ height: 40 }} />
                <div>
                  <div style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Account</div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: accessStatus.isActive ? "#52c41a" : "#ff4d4f" }}>
                    {accessStatus.isActive ? <><CheckCircleOutlined style={{ marginRight: 4 }} />Active</> : <><StopOutlined style={{ marginRight: 4 }} />Inactive</>}
                  </span>
                </div>
                {accessStatus.holdDate && (
                  <>
                    <Divider type="vertical" style={{ height: 40 }} />
                    <div>
                      <div style={{ fontSize: 11, color: "#8091a7", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Hold Since</div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {moment.utc(accessStatus.holdDate).local().format("DD MMM YYYY, hh:mm A")}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div>
                {accessStatus.billingStatus === "Active" ? (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => { setHoldReason(""); setHoldModal(true); }}
                  >
                    <StopOutlined style={{ marginRight: 4 }} /> Put on Hold
                  </Button>
                ) : (
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => { setHoldReason(""); setHoldModal(true); }}
                  >
                    <CheckCircleOutlined style={{ marginRight: 4 }} /> Un Hold
                  </Button>
                )}
              </div>
            </div>
            {accessStatus.holdReason && (
              <div style={{ marginTop: 12, padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                <span style={{ fontSize: 12, color: "#8091a7" }}>Hold Reason: </span>
                <span style={{ fontWeight: 500 }}>{accessStatus.holdReason}</span>
              </div>
            )}
          </AntCard>

          {/* Auto Hold Config */}
          <AntCard
            size="small"
            className="mb-3"
            bodyStyle={{ padding: "16px 20px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <StopOutlined style={{ fontSize: 18, color: "#8091a7" }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Auto Hold Configuration</div>
                  <div style={{ fontSize: 12, color: "#8091a7" }}>Automatically hold account on overdue payments</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <Switch
                  checked={autoHoldEnabled}
                  onChange={(checked) => {
                    setAutoHoldEnabled(checked);
                    if (!checked) setAutoHoldDays(null);
                  }}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
                {autoHoldEnabled && (
                  <Select
                    value={autoHoldDays ?? undefined}
                    onChange={(val) => setAutoHoldDays(val)}
                    style={{ width: 140 }}
                    placeholder="Select days"
                    size="small"
                    options={[5, 10, 15, 20, 25, 30, 45, 60].map((d) => ({
                      value: d,
                      label: `After ${d} days`,
                    }))}
                  />
                )}
                <Button
                  color="primary"
                  size="sm"
                  onClick={saveAutoHoldConfig}
                  disabled={autoHoldSaving}
                >
                  {autoHoldSaving ? <LoadingSpinner size="sm" inline /> : "Save"}
                </Button>
              </div>
            </div>
          </AntCard>
        </Col>

        {/* Right: Activity History */}
        <Col lg="5">
          <AntCard
            size="small"
            title={<span style={{ fontWeight: 600 }}><ClockCircleOutlined style={{ marginRight: 8 }} />Activity History</span>}
            bodyStyle={{ padding: "12px 16px", maxHeight: 360, overflowY: "auto" }}
          >
            {accessLogs.length > 0 ? (
              <Timeline
                className="cp-timeline"
                style={{ marginTop: 8 }}
                items={accessLogs.slice(0, 20).map((log) => ({
                  color: log.actionType?.includes("UNHOLD") ? "green" : "red",
                  children: (
                    <div style={{ paddingBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <Tag
                          color={log.actionType?.includes("UNHOLD") ? "success" : "error"}
                          style={{ fontSize: 11 }}
                        >
                          {log.actionType?.replace(/_/g, " ")}
                        </Tag>
                        <span style={{ fontSize: 11, color: "#8091a7" }}>
                          {moment.utc(log.performedAt).local().format("DD MMM YYYY, hh:mm A")}
                        </span>
                      </div>
                      {log.reason && (
                        <div style={{ fontSize: 12, color: "#526484", marginTop: 4 }}>
                          {log.reason}
                        </div>
                      )}
                      {(log.previousStatus || log.newStatus) && (
                        <div style={{ fontSize: 11, color: "#8091a7", marginTop: 2 }}>
                          {log.previousStatus} <span style={{ margin: "0 4px" }}>→</span> {log.newStatus}
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            ) : (
              <Empty description="No activity yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </AntCard>
        </Col>
      </Row>
    );
  };

  // =============================================
  // Tabs config
  // =============================================
  const tabItems = [
    {
      key: "billing",
      label: <span><CreditCardOutlined /> Billing</span>,
      children: renderBilling(),
    },
    {
      key: "modules",
      label: <span><AppstoreOutlined /> Application Modules</span>,
      children: renderModules(),
    },
    {
      key: "userLimits",
      label: <span><TeamOutlined /> User Limits</span>,
      children: renderUserLimits(),
    },
    {
      key: "coupons",
      label: <span><TagOutlined /> Coupons</span>,
      children: renderCoupons(),
    },
    {
      key: "access",
      label: <span><SafetyOutlined /> Access{isOnHold && <AntBadge status="error" style={{ marginLeft: 2 }} />}</span>,
      children: renderAccessControl(),
    },
    {
      key: "invoices",
      label: <span><FileTextOutlined /> Invoice Report</span>,
      children: renderInvoices(),
    },
    {
      key: "payments",
      label: <span><WalletOutlined /> Payments</span>,
      children: renderPayments(),
    },
  ];

  const renderControlPanelContent = () => (
    <>
      {!embedded && (
        <>
          {/* Header */}
          <BlockHead size="sm">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h3" page>
                  <span className="cp-header-title" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {customer.partyName}
                    {isDraft ? (
                      <Tag color="warning" style={{ fontSize: 12 }} className="cp-status-tag">DRAFT</Tag>
                    ) : isOnHold ? (
                      <Tag color="error" style={{ fontSize: 12 }} className="cp-status-tag">ON HOLD</Tag>
                    ) : (
                      <Tag color="success" style={{ fontSize: 12 }} className="cp-status-tag">ACTIVE</Tag>
                    )}
                  </span>
                </BlockTitle>
              </BlockHeadContent>
              <BlockHeadContent>
                <div style={{ display: "flex", gap: 8 }}>
                  {isDraft && (
                    <Button color="success" onClick={handleActivateCustomer}>
                      <Icon name="check-circle" />
                      <span>Activate</span>
                    </Button>
                  )}
                  <Button color="primary" className="btn-dim" onClick={() => navigate(-1)}>
                    <Icon name="arrow-left" />
                    <span>Back</span>
                  </Button>
                </div>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          {/* Profile Card */}
          <Block>
          <AntCard size="small" style={{ marginBottom: 16 }} className="cp-profile-card">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
              {/* Avatar */}
              <div
                className="cp-avatar"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 22,
                  fontWeight: 700,
                  flexShrink: 0,
                  cursor: "default",
                }}
              >
                {customer.partyName?.charAt(0)?.toUpperCase()}
              </div>

              {/* Info chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", flex: 1 }}>
                <div className="cp-info-chip">
                  <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Code</div>
                  <div style={{ fontWeight: 600 }}>{customer.partyCode}</div>
                </div>
                <div className="cp-info-chip">
                  <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Type</div>
                  <Tag color="processing">{customer.partyType}</Tag>
                </div>
                {customer.adminEmail && (
                  <div className="cp-info-chip">
                    <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Email</div>
                    <div><MailOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />{customer.adminEmail}</div>
                  </div>
                )}
                {customer.phoneNumber && (
                  <div className="cp-info-chip">
                    <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Phone</div>
                    <div><PhoneOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />{customer.phoneNumber}</div>
                  </div>
                )}
                {(customer.countryName || customer.stateName) && (
                  <div className="cp-info-chip">
                    <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Location</div>
                    <div><GlobalOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />{[customer.stateName, customer.countryName].filter(Boolean).join(", ")}</div>
                  </div>
                )}
                {customer.currencyCode && (
                  <div className="cp-info-chip">
                    <div className="text-soft" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Currency</div>
                    <Tag>{customer.currencyCode}</Tag>
                  </div>
                )}
              </div>
            </div>
          </AntCard>
        </Block>
        </>
      )}

        {/* Tabs */}
        <Block>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
            type="line"
            size="large"
            style={{ background: "#fff", padding: "12px 20px", borderRadius: 4, border: "1px solid #e5e9f2" }}
            tabBarStyle={{ marginBottom: 16 }}
          />
        </Block>

        {/* Hold/Unhold Modal */}
        <Modal
          isOpen={holdModal}
          toggle={() => !holdLoading && setHoldModal(false)}
          size="sm"
        >
          <ModalBody>
            <div className="cp-modal-icon" style={{ textAlign: "center", marginBottom: 16 }}>
              {accessStatus?.billingStatus === "Active" ? (
                <StopOutlined style={{ fontSize: 40, color: "#ff4d4f" }} />
              ) : (
                <CheckCircleOutlined style={{ fontSize: 40, color: "#52c41a" }} />
              )}
            </div>
            <h5 className="title text-center mb-3">
              {accessStatus?.billingStatus === "Active" ? "Put on Hold" : "Remove Hold"}
            </h5>
            <p className="text-soft text-center" style={{ fontSize: 13 }}>
              {accessStatus?.billingStatus === "Active"
                ? "This will restrict the customer's access to all services."
                : "This will restore the customer's access to all services."}
            </p>
            <div className="form-group">
              <label className="form-label">Reason (optional)</label>
              <Input.TextArea
                rows={3}
                maxLength={500}
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="Enter reason..."
              />
            </div>
            <div className="mt-3" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button
                color={accessStatus?.billingStatus === "Active" ? "danger" : "primary"}
                size="sm"
                onClick={() => handleHoldUnhold(accessStatus?.billingStatus === "Active" ? "hold" : "unhold")}
                disabled={holdLoading}
              >
                {holdLoading ? <LoadingSpinner size="sm" inline /> : accessStatus?.billingStatus === "Active" ? "Confirm Hold" : "Confirm Unhold"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setHoldModal(false)} disabled={holdLoading}>
                Cancel
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Link Coupon Modal */}
        <Modal
          isOpen={linkCouponModal}
          toggle={() => !linkCouponLoading && setLinkCouponModal(false)}
          size="md"
        >
          <ModalBody>
            <h5 className="title mb-4">Link Coupon to Customer</h5>
            <div className="form-group mb-3">
              <label className="form-label">Select Coupon</label>
              <Select
                placeholder="Search and select a coupon..."
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="label"
                value={selectedCouponId}
                onChange={(val) => setSelectedCouponId(val)}
                options={couponDropdown}
                size="large"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Reference (optional)</label>
              <Input
                placeholder="e.g., Sales Team, Promotion Campaign"
                value={couponReferenceFrom}
                onChange={(e) => setCouponReferenceFrom(e.target.value)}
              />
            </div>
            <div className="mt-4" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" onClick={handleLinkCoupon} disabled={linkCouponLoading || !selectedCouponId}>
                {linkCouponLoading ? <LoadingSpinner size="sm" inline /> : "Link Coupon"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setLinkCouponModal(false)} disabled={linkCouponLoading}>
                Cancel
              </Button>
            </div>
          </ModalBody>
        </Modal>

    </>
  );

  if (embedded) {
    return renderControlPanelContent();
  }

  return (
    <React.Fragment>
      <Head title="Customer Control Panel" />
      <Content>
        {renderControlPanelContent()}
      </Content>
    </React.Fragment>
  );
};

export default CustomerControlPanel;
